import { DatePipe } from '@angular/common';
import { Component, computed, ElementRef, Injector, OnInit, Renderer2, signal, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { AbstractControl, FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { BodyComponentObject, ButtonsComponentObject, FooterComponentObject, HeaderComponentObject, TemplateMessageObject } from '@trendbuild/trend-cloud-api';
import { Workbook } from 'exceljs';
import { AbstractComponent, AudienceCounts, AudienceSelection, AudienceSummary, CacheKeyEnum, Campaign, CampaignAudienceFile, CampaignAudienceFileService, CampaignAudienceRow, CampaignAudienceSourceEnum, CampaignAudienceTypeEnum, CampaignExecutionTypeEnum, CampaignService, CampaignStatusEnum, Channel, ChannelService, ChannelTypeEnum, CloudAPIData, Company, ConfirmationComponent, ContactGroup, ContactGroupService, ContactService, FirebaseService, isBrazilianPhone, isValidPhone, RCSData, RCSMessageTypeEnum, sanitizePhone, Tag, TagService, Traduct, UploadTypeEnum, UtilHelper, InsightsService, walletBillingCategoryFor, WalletBillingCategoryEnum, WalletService, WalletSummary, WalletTypeEnum, walletTypeForChannel, ZAPIData } from 'lib-trend-core';
import * as Papa from 'papaparse';
import { BehaviorSubject, catchError, debounceTime, distinctUntilChanged, of, Subject, switchMap, takeUntil, tap } from 'rxjs';
import { CampaingSaveConfirmData, CampaingSaveConfirmModalComponent } from '../campaing-save-confirm-modal/campaing-save-confirm-modal.component';
import { RcsVariableOption } from '../../rcs-message/rcs-message-editor/rcs-message-editor.component';
import { applyRcsTypeValidators, buildRcsDataGroup } from '../../rcs-message/rcs-message-form.util';

type Step = { title: string; subtitle: string };
export type ChannelRef = { _id: string; name: string; type: string };

@Component({
    selector: 'app-campaign-form',
    templateUrl: './campaign-form.v2.component.html',
    styleUrls: ['./campaign-form.v2.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class CampaignFormComponent extends AbstractComponent implements OnInit {

  @ViewChild('messageTextarea') messageTextarea!: ElementRef<HTMLTextAreaElement>;
  @ViewChild("emojiContainer", { static: false, read: ElementRef }) emojiContainer!: ElementRef
  @ViewChild('localZAPIFile', { static: false }) inputFileZAPIFile!: ElementRef;
  @ViewChild('localCloudAPIFile', { static: false }) inputFileCloudAPIFile!: ElementRef;
  @ViewChild('localAudienceFile', { static: false }) inputAudienceFile!: ElementRef<HTMLInputElement>;

  readonly META_BODY_CHARACTER_LIMIT = 1024;

  /** The variable the RCS composer offers in a campaign: the contact's name (DEV-2053). */
  readonly rcsVariables: RcsVariableOption[] = [
    { token: 'NOME', labelKey: 'campaign.campaignForm.contactName', icon: 'mdi-account' },
  ];

  variableOptions = [
    { icon: 'person', text: 'Nome do contato', action: (section: 'headers' | 'bodys', index: number) => this.selectVarNameContactLead(section, index) },
    { icon: 'person', text: 'Telefone do contato', action: (section: 'headers' | 'bodys', index: number) => this.selectVarPhoneContactLead(section, index) },
    { icon: 'person', text: 'Email do contato', action: (section: 'headers' | 'bodys', index: number) => this.selectVarEmailContactLead(section, index) },
  ];

  steps: Step[] = [
    { title: 'campaign.form.stepChannelTitle', subtitle: 'campaign.form.stepChannelSubtitle' },
    { title: 'campaign.form.stepMessageTitle', subtitle: 'campaign.form.stepMessageSubtitle' },
    { title: 'campaign.form.stepRecipientsTitle', subtitle: 'campaign.form.stepRecipientsSubtitle' },
    { title: 'campaign.form.stepScheduleTitle', subtitle: 'campaign.form.stepScheduleSubtitle' },
    { title: 'campaign.form.stepReviewTitle', subtitle: 'campaign.form.stepReviewSubtitle' },
  ];

  stepIndex = signal(0);

  private clampedIndex = computed(() => {
    const total = this.steps.length;
    return total ? Math.min(Math.max(this.stepIndex(), 0), total - 1) : 0;
  });

  private currentStepSignal = computed(() => this.clampedIndex() + 1);


  // --- Dispatch summary -----------------------------------------------------

  audienceSummary: AudienceSummary | null = null;
  audienceSummaryLoading = false;
  audienceSummaryFailed = false;
  audienceCounts: AudienceCounts = { all: 0, tags: {}, groups: {} };
  readonly audienceCountsLoading = new Set<string>();
  private readonly audienceSelection$ = new Subject<AudienceSelection>();

  readonly AudienceSource = CampaignAudienceSourceEnum;
  readonly AUDIENCE_FILE_MAX_ROWS = 50000;
  readonly AUDIENCE_FILE_MAX_BYTES = 5 * 1024 * 1024;
  readonly AUDIENCE_PREVIEW_ROWS = 10;

  audienceFile: CampaignAudienceFile = null;
  availableAudienceFiles: Array<CampaignAudienceFile> = [];
  /** The file as the operator chose it, kept so the country-prefix toggle can re-normalize it. */
  private audienceSourceFile: File = null;
  private audienceFileRawRows: Array<Record<string, any>> = [];
  audienceFileRows: Array<CampaignAudienceRow> = [];
  audienceFileMappings: { phone?: string; name?: string; email?: string } = {};
  audienceFileColumns: Array<{ csvColumn: string; sample: string }> = [];
  audienceFileStep = 0;
  audienceFileName = '';
  audienceFileSize = '';
  audienceFileTotalRows = 0;
  audienceFileValidRows = 0;
  /** Rows the toggle above fixes: a usable number the channel cannot reach without the country code. */
  audienceFileMissingCountryCodeRows = 0;
  /** Rows only a corrected file fixes: the cell is not a number we can dispatch to. */
  audienceFileInvalidPhoneRows = 0;
  audienceShowOnlyErrors = false;
  audienceApplyCountryPrefix = false;
  /** Reading back a list uploaded earlier, so its card can show the same preview. */
  audienceFilePreviewLoading = false;
  audienceFilePreviewFailed = false;
  /**
   * What Meta charges per delivered message for the selected template, in the
   * WABA's own currency — the only per-template rate we can actually observe.
   *
   * There is no forward rate card in the API: the template object carries
   * category, status and quality, never a price. So this is read from Meta's
   * template analytics (`cost_per_delivered`), which means it is the rate this
   * template was ACTUALLY billed at recently. A template never sent yet has no
   * cost history and stays null — quoting a number there would be inventing one.
   *
   * ORDINARY Cloud API channels only. A credit-line channel is priced by the
   * WhatsApp wallet (see `cloudApiBilledByWallet`): Meta is never asked, and
   * these four fields stay at rest.
   */
  cloudApiUnitPrice: number | null = null;
  /**
   * Billing currency of the WABA. NULL means we do not know it — never BRL by
   * default. Assuming reais here is how a tenant billed in dollars gets a
   * dollar figure wearing an R$ sign, which is the defect this whole change
   * exists to remove.
   */
  cloudApiCurrency: string | null = null;
  cloudApiPriceLoading = false;
  /** The analytics call itself failed — distinct from "the WABA has no currency". */
  cloudApiPriceFailed = false;

  /**
   * Estimated spend for the whole dispatch. A GETTER, not a field: the old
   * field was only written inside the audience-summary subscription, so picking
   * a different template never moved it — the screen kept quoting the previous
   * template's price. It now derives from the same price the rail shows —
   * the wallet's or Meta's, whichever bills the channel — so the two can never
   * disagree.
   */
  get cost(): number { return this.summaryEstimatedCost; }
  audienceDescription: string = '';

  loadingSpinnerSubject = new BehaviorSubject<boolean>(false);
  loadingSpinner$ = this.loadingSpinnerSubject.asObservable();

  showContainerEmoji: boolean = false;
  createButton: boolean = false;

  campaign: Campaign;
  selectedChannel: Channel;
  selectedTemplate: TemplateMessageObject;
  file: File;
  buttonWithExampleIndices: number[] = [];

  /** Tipos de canal que não disparam campanha — ver `dispatchesCampaign`. */
  private static readonly CAMPAIGN_INELIGIBLE_CHANNELS: ReadonlyArray<ChannelTypeEnum> = [
    ChannelTypeEnum.INSTAGRAM_API,
    ChannelTypeEnum.MESSENGER_API,
    ChannelTypeEnum.VOIP_API,
    ChannelTypeEnum.WEBCHAT_API,
  ];

  channels: Array<Channel>;
  allChannel: Array<Channel> = new Array<Channel>();

  tags: Array<Tag>;
  allTags: Array<Tag> = new Array<Tag>();

  contactGroups: Array<ContactGroup>;
  allGroups: Array<ContactGroup> = new Array<ContactGroup>();

  templates: Array<TemplateMessageObject>;
  allTemplates: Array<TemplateMessageObject> = new Array<TemplateMessageObject>();

  filteredOptions: Array<{ icon: string, text: string, action: Function }> = this.variableOptions;

  filterControlChannel: FormControl = new FormControl('');
  filterControlTag: FormControl = new FormControl('');
  filterControlGroup: FormControl = new FormControl('');
  filterControlTemplate: FormControl = new FormControl('');

  translator!: Traduct;

  private clearTimeout;
  private clickOutsideListener;

  get currentStep(): number {
    return this.currentStepSignal();
  }

  constructor(
    injector: Injector,
    private channelService: ChannelService,
    private renderer: Renderer2,
    private dialog: MatDialog,
    private firebaseService: FirebaseService,
    private contactService: ContactService,
    private contactGroupService: ContactGroupService,
    private tagService: TagService,
    private campaignService: CampaignService,
    private datePipe: DatePipe,
    private modalAlert: MatDialog,
    private walletService: WalletService,
    private insightsService: InsightsService,
    private campaignAudienceFileService: CampaignAudienceFileService,

  ) {
    super(injector);
    this.campaign = {
      metadata: {
        cloudAPIData: {} as CloudAPIData,
        zapiData: {} as ZAPIData,
        rcsData: {} as RCSData,
      }
    } as Campaign;
    this.createForm();
  }

  ngOnInit(): void {
    this.initAudienceSummaryPipeline();
    this.loadAudienceCounts();
    this.loadWalletSummaries();
    this.loadAllChannels();
    this.fetchFilterChannel();

    this.loadAllTags();
    this.fetchFilterTags();

    this.loadAllContactGroups();
    this.fetchFilterGroups();

    const idCampaign = this.getParam('id');
    if (idCampaign) {
      this.isNew = false;
      this.campaignService.getById(idCampaign).subscribe((campaign: Campaign) => {
        this.campaign = campaign;
        this.selectedChannel = campaign.channel;

        const formObject = {
          ...this.campaign,
          channel: this.selectedChannel._id,
          rcsDataGroup: this.campaign.metadata.rcsData
        };

        if (CampaignAudienceTypeEnum.TAGS === campaign.audienceType) {
          formObject['tags'] = campaign.audienceTags.map(t => t._id);
          formObject['ignoredTags'] = campaign.ignoredAudienceTags?.map(t => t._id) || [];
          this.checkTags();
        }

        if (CampaignAudienceTypeEnum.CONTACT_GROUP === campaign.audienceType) {
          formObject['contactGroups'] = campaign.audienceContactGroup.map(t => t._id);
          formObject['ignoredContactGroups'] = campaign.ignoredAudienceContactGroup?.map(t => t._id)
          this.checkContactGroups();
        }

        if (CampaignAudienceTypeEnum.FILE === campaign.audienceType) {
          const audienceFile = campaign.audienceFile as CampaignAudienceFile;
          formObject['audienceSource'] = CampaignAudienceSourceEnum.FILE_EXISTING;
          formObject['audienceFile'] = audienceFile?._id ?? campaign.audienceFile;
          this.audienceFile = audienceFile ?? null;
          this.audienceFileName = audienceFile?.filename ?? '';
          this.audienceFileTotalRows = audienceFile?.totalRows ?? campaign.leadCount ?? 0;
          this.audienceFileValidRows = audienceFile?.validRows ?? campaign.leadCount ?? 0;
          this.loadAvailableAudienceFiles();
          this.loadExistingAudiencePreview(String(formObject['audienceFile']));
        }

        if (CampaignExecutionTypeEnum.SCHEDULED === campaign.executionType) {
          formObject['scheduledDay'] = this.datePipe.transform(campaign.scheduledDate, 'yyyy-MM-dd');
          formObject['scheduledHour'] = this.datePipe.transform(campaign.scheduledDate, 'HH:mm');
        }

        this.formGroup.patchValue(formObject);

        if (campaign.channel.type === ChannelTypeEnum.RCS_API && campaign.metadata.rcsData) {
          // Rebuilt rather than patched: patchValue fills no FormArray item, and the group must carry
          // the validators of the saved format (RcsMessageModule).
          this.formGroup.setControl('rcsDataGroup', buildRcsDataGroup(campaign.metadata.rcsData));
        }

        if (campaign.channel.type === ChannelTypeEnum.CLOUD_API) {
          const { template, headerParams, bodyParams, buttonParams } = campaign.metadata.cloudAPIData;
          this.selectedTemplate = template;
          // Reopening an existing dispatch must quote a price too, not only the
          // path where the operator picks the template by hand.
          this.loadCloudApiTemplatePrice();

          (this.formGroup.get('cloudAPIDataGroup.headerParams') as FormArray).clear();
          (this.formGroup.get('cloudAPIDataGroup.bodyParams') as FormArray).clear();
          (this.formGroup.get('cloudAPIDataGroup.buttonParams') as FormArray).clear();

          if (!!headerParams && headerParams.length > 0) {
            for (let header of headerParams) {
              (this.formGroup.get('cloudAPIDataGroup.headerParams') as FormArray).push(
                new FormGroup({
                  value: new FormControl(header, [Validators.required]),
                })
              );
            }
          }

          if (!!bodyParams && bodyParams.length > 0) {
            for (let body of bodyParams) {
              (this.formGroup.get('cloudAPIDataGroup.bodyParams') as FormArray).push(
                new FormGroup({
                  value: new FormControl(body, [Validators.required]),
                })
              );
            }
          }

          this.buttonWithExampleIndices = [];
          if (this.buttonsComponent) {
            if (this.buttonsComponent?.buttons?.length > 0) {
              this.buttonsComponent.buttons.forEach((button, index) => {
                this.buttonWithExampleIndices.push(index);
                const disabled = !button?.['example'];
                const value = !disabled ? buttonParams[index] : button.text || '';
                (this.formGroup.get('cloudAPIDataGroup.buttonParams') as FormArray).push(
                  new FormGroup({
                    value: new FormControl({ value, disabled }, [Validators.required]),
                  })
                );
              });
            }
          }
        }

        if (campaign.channel.type === ChannelTypeEnum.Z_API) {
          const zapiData = campaign.metadata?.zapiData as ZAPIData;
          const isButtons =
            zapiData?.messageType === 'buttons' ||
            !!zapiData?.buttonsMessage ||
            (Array.isArray(zapiData?.buttonActions) && zapiData.buttonActions.length > 0);

          if (isButtons) {
            this.getZAPIFormGroup.get('messageType')?.setValue('buttons');
            this.getZAPIFormGroup.get('buttonsTitle')?.setValue(zapiData?.buttonsTitle || '');
            this.getZAPIFormGroup.get('buttonsMessage')?.setValue(zapiData?.buttonsMessage || '');
            this.getZAPIFormGroup.get('buttonsFooter')?.setValue(zapiData?.buttonsFooter || '');

            const actionsArray = this.getZAPIFormGroup.get('buttonActions') as FormArray;
            if (actionsArray) {
              actionsArray.clear();
              if (Array.isArray(zapiData?.buttonActions)) {
                zapiData.buttonActions.forEach((action: any) => {
                  actionsArray.push(
                    new FormGroup({
                      type: new FormControl(action?.type || '', [Validators.required]),
                      label: new FormControl(action?.label || '', [Validators.required]),
                      phone: new FormControl(action?.phone || ''),
                      url: new FormControl(action?.url || ''),
                    })
                  );
                });
              }
            }
          } else {
            this.getZAPIFormGroup.get('messageType')?.setValue('text');
            this.getZAPIFormGroup.get('message').setValue(zapiData?.message);
            this.getZAPIFormGroup.get('midia').setValue(zapiData?.midia);
            this.getZAPIFormGroup.get('type').setValue(zapiData?.type);
            this.getZAPIFormGroup.get('contentType').setValue(zapiData?.contentType);
          }
        }

      });
    } else {
      this.isNew = true;
    }
  }

  private getMaxReachableStepIndex(): number {
    let max = 0;

    if (!this.disableNextChannel()) {
      max = 1;
    } else {
      return max;
    }

    if (!this.disableNextMessage()) {
      max = 2;
    } else {
      return max;
    }

    if (!this.disableNextRecipient()) {
      max = 3;
    } else {
      return max;
    }

    if (!this.disableNextScheduling()) {
      max = 4;
    }

    return max;
  }

  canGoToStep(targetIndex: number): boolean {
    if (!Number.isFinite(targetIndex)) return false;
    if (targetIndex < 0 || targetIndex > this.steps.length - 1) return false;
    if (targetIndex <= this.stepIndex()) return true;

    return targetIndex <= this.getMaxReachableStepIndex();
  }

  goToStep(targetIndex: number): void {
    if (!this.canGoToStep(targetIndex)) return;

    const clamped = Math.min(Math.max(targetIndex, 0), this.steps.length - 1);
    const was = this.stepIndex();
    this.stepIndex.set(clamped);

    if (was !== 4 && clamped === 4) {
      this.calculateAudienceAndLeads();
    }
  }

  nextStep(): void {
    const next = Math.min(this.stepIndex() + 1, this.steps.length - 1);
    this.goToStep(next);
  }

  previousStep(): void {
    const prev = Math.max(this.stepIndex() - 1, 0);
    this.goToStep(prev);
  }

  private createForm(): void {
    this.formGroup = new FormGroup({
      name: new FormControl(this.campaign.name, [Validators.required, Validators.maxLength(50)]),
      channel: new FormControl(this.selectedChannel?._id || '', [Validators.required]),

      // Which of the three cards on the Recipients step is selected. UI-only:
      // both file cards persist as audienceType FILE.
      audienceSource: new FormControl(CampaignAudienceSourceEnum.BASE),
      audienceType: new FormControl(CampaignAudienceTypeEnum.ALL_LEADS),
      ignoredTags: new FormControl([]),
      ignoredContactGroups: new FormControl([]),
      tags: new FormControl([]),
      contactGroups: new FormControl([]),
      // _id of the CampaignAudienceFile backing a FILE audience.
      audienceFile: new FormControl(null),
      executionType: new FormControl(CampaignExecutionTypeEnum.IMEDIATE, [Validators.required]),
      scheduledDay: new FormControl(''),
      scheduledHour: new FormControl(''),
      delayInSeconds: new FormControl(0),
      enabled: new FormControl(false, []),

      // For CLOUD_API
      cloudAPIDataGroup: new FormGroup({
        template: new FormControl(null, []),
        headerParams: new FormArray([]),
        buttonParams: new FormArray([]),
        bodyParams: new FormArray([]),
      }),

      // For Z-API
      zapiDataGroup: new FormGroup({
        messageType: new FormControl('text', []),
        type: new FormControl('', []),
        message: new FormControl('', []),
        midia: new FormControl('', []),
        contentType: new FormControl('', []),
        buttonsTitle: new FormControl('', []),
        buttonsMessage: new FormControl('', []),
        buttonsFooter: new FormControl('', []),
        buttonActions: new FormArray([]),
      }),

      // For RCS — the group the shared RCS composer edits (RcsMessageModule)
      rcsDataGroup: buildRcsDataGroup(),
    });
    this.calculateAudienceAndLeads();
    this.formGroup.get('audienceType').valueChanges.subscribe(value => {
      if (this.isAudienceTypeTags()) {
        this.formGroup.get('tags').setValidators(Validators.required);
        this.formGroup.get('contactGroups').setValidators(null);
      } else if (this.isAudienceTypeContactGroups()) {
        this.formGroup.get('tags').setValidators(null);
        this.formGroup.get('contactGroups').setValidators(Validators.required);
      } else {
        this.formGroup.get('tags').setValidators(null);
        this.formGroup.get('contactGroups').setValidators(null);
      }
      this.formGroup.get('tags').setValue([]);
      this.formGroup.get('contactGroups').setValue([]);
      this.formGroup.get('tags').updateValueAndValidity();
      this.formGroup.get('contactGroups').updateValueAndValidity();

      // O painel de exclusões só existe em "Todos os contatos". Sair dele tem
      // de limpar as listas, não apenas escondê-las: o valor continua no
      // formulário, vai no corpo do save e é aplicado na materialização
      // (`buildContactQuery` cruza os ignorados em todos os modos de base) —
      // ou seja, o público encolheria por um filtro que ninguém vê na tela.
      if (!this.isAudienceTypeAllLeads()) {
        this.formGroup.get('ignoredTags').setValue([]);
        this.formGroup.get('ignoredContactGroups').setValue([]);
      }

      // Leaving the file cards drops the file too. This subscriber already wipes
      // tags and groups on every change; not wiping the file here would leave a
      // campaign carrying a list it no longer uses.
      if (value !== CampaignAudienceTypeEnum.FILE) {
        this.clearAudienceFile();
      }
    });
    this.formGroup.get('executionType').valueChanges.subscribe(value => {
      if (this.isExecutionTypeScheduled()) {
        this.formGroup.get('scheduledDay').setValidators(Validators.required);
        this.formGroup.get('scheduledHour').setValidators(Validators.required);
      } else {
        this.formGroup.get('scheduledDay').setValidators(null);
        this.formGroup.get('scheduledHour').setValidators(null);
      }
      this.formGroup.get('scheduledDay').updateValueAndValidity();
      this.formGroup.get('scheduledHour').updateValueAndValidity();
    });
  }

  isAudienceTypeAllLeads(): boolean {
    return this.formGroup.get('audienceType').value === CampaignAudienceTypeEnum.ALL_LEADS;
  }

  isAudienceTypeTags(): boolean {
    return this.formGroup.get('audienceType').value === CampaignAudienceTypeEnum.TAGS;
  }

  isAudienceTypeContactGroups(): boolean {
    return this.formGroup.get('audienceType').value === CampaignAudienceTypeEnum.CONTACT_GROUP;
  }

  // ---------------------------------------------------------------------------
  // Recipients step — audience source
  //
  // Three cards: the contact base, a file uploaded now, or a list uploaded
  // before. The last two are the same persisted audienceType (FILE) and differ
  // only in how the file is chosen, so the card selection is tracked separately
  // in `audienceSource` and never leaves the browser.
  //
  // A file audience is EPHEMERAL: its rows are dispatch recipients for this one
  // campaign and are never written to the contact base. A number becomes a real
  // contact only by replying.
  // ---------------------------------------------------------------------------

  isAudienceSourceBase(): boolean {
    return this.formGroup.get('audienceSource').value === CampaignAudienceSourceEnum.BASE;
  }

  isAudienceSourceFileNew(): boolean {
    return this.formGroup.get('audienceSource').value === CampaignAudienceSourceEnum.FILE_NEW;
  }

  isAudienceSourceFileExisting(): boolean {
    return this.formGroup.get('audienceSource').value === CampaignAudienceSourceEnum.FILE_EXISTING;
  }

  isAudienceTypeFile(): boolean {
    return this.formGroup.get('audienceType').value === CampaignAudienceTypeEnum.FILE;
  }

  /**
   * Card click. Picking a base card restores the last base audience type;
   * picking a file card switches the persisted type to FILE.
   */
  selectAudienceSource(source: CampaignAudienceSourceEnum): void {
    this.formGroup.get('audienceSource').setValue(source);

    if (source === CampaignAudienceSourceEnum.BASE) {
      if (this.isAudienceTypeFile()) {
        this.formGroup.get('audienceType').setValue(CampaignAudienceTypeEnum.ALL_LEADS);
      }
      return;
    }

    this.formGroup.get('audienceType').setValue(CampaignAudienceTypeEnum.FILE);
    // Both file cards share the file state: carried across, one file's columns
    // would sit over another file's rows.
    this.clearAudienceFile();

    if (source === CampaignAudienceSourceEnum.FILE_EXISTING) {
      this.loadAvailableAudienceFiles();
    }
  }

  /** Base sub-mode (all / tags / groups) — unchanged behaviour, kept as dropdowns. */
  selectAudienceType(audienceType: CampaignAudienceTypeEnum): void {
    this.formGroup.get('audienceType').setValue(audienceType);
  }

  loadAvailableAudienceFiles(): void {
    this.campaignAudienceFileService.listAvailable()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (files: Array<CampaignAudienceFile>) => this.availableAudienceFiles = files ?? [],
        error: () => this.alertService.error(this.t('campaign.campaignForm.audienceFileListError')),
      });
  }

  onSelectExistingAudienceFile(idAudienceFile: string): void {
    const file = (this.availableAudienceFiles ?? []).find(f => f._id === idAudienceFile);
    if (!file) return;

    this.audienceFile = file;
    this.audienceFileRows = [];
    this.audienceFileColumns = [];
    this.audienceFileTotalRows = file.totalRows;
    this.audienceFileValidRows = file.validRows;
    // The numbers in a stored list are already final: the prefix belongs to the
    // upload that produced it, and carrying the toggle over from a previous file
    // would show a spelling this list does not have.
    this.audienceApplyCountryPrefix = false;
    this.formGroup.get('audienceFile').setValue(file._id);
    this.calculateAudienceAndLeads();
    this.loadExistingAudiencePreview(idAudienceFile);
  }

  /**
   * Reads back the rows of a list uploaded earlier, so its card previews what
   * will be dispatched instead of only a row count.
   *
   * The picker carries counters alone, so the descriptor is fetched for the
   * normalized CSV — the very file the dispatch parses. A preview that cannot be
   * read leaves the counters from the descriptor standing: failing to draw a
   * table is no reason to block a list that is perfectly usable.
   */
  private loadExistingAudiencePreview(idAudienceFile: string): void {
    this.audienceFilePreviewLoading = true;
    this.audienceFilePreviewFailed = false;

    this.campaignAudienceFileService.getById(idAudienceFile)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: async (file: CampaignAudienceFile) => {
          try {
            this.audienceFile = file;
            this.audienceFileRawRows = await this.parseAudienceCsv(file.normalizedUrl);
            this.audienceFileColumns = this.audienceColumnsOf(this.audienceFileRawRows);
            this.audienceFileMappings = {
              phone: file.mappings?.['phone'],
              name: file.mappings?.['name'],
              email: file.mappings?.['email'],
            };
            this.buildAudienceRows();
          } catch {
            this.audienceFilePreviewFailed = true;
          } finally {
            this.audienceFilePreviewLoading = false;
          }
        },
        error: () => {
          this.audienceFilePreviewLoading = false;
          this.audienceFilePreviewFailed = true;
        },
      });
  }

  /**
   * Reads the chosen file in the browser.
   *
   * `.csv` goes through papaparse and `.xlsx` through exceljs — both already
   * platform dependencies. Whichever it is, the rows are normalized to a single
   * UTF-8 CSV that is what the backend will parse, so the two formats reach the
   * server identically and no spreadsheet parser is needed there.
   */
  async onSelectAudienceFile(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input?.files?.[0];
    if (!file) return;

    const extension = (file.name.split('.').pop() ?? '').toLowerCase();
    if (extension !== 'csv' && extension !== 'xlsx') {
      this.alertService.error(this.t('campaign.campaignForm.audienceFileInvalidExtension'));
      input.value = '';
      return;
    }

    if (file.size > this.AUDIENCE_FILE_MAX_BYTES) {
      this.alertService.error(this.t('campaign.campaignForm.audienceFileTooLarge', {
        max: String(this.AUDIENCE_FILE_MAX_BYTES / (1024 * 1024)),
      }));
      input.value = '';
      return;
    }

    this.loadingSpinnerSubject.next(true);
    try {
      const rawRows = extension === 'csv'
        ? await this.parseAudienceCsv(file)
        : await this.parseAudienceXlsx(file);

      if (!rawRows.length) {
        this.alertService.error(this.t('campaign.campaignForm.audienceFileEmpty'));
        this.clearAudienceFile(input);
        return;
      }

      if (rawRows.length > this.AUDIENCE_FILE_MAX_ROWS) {
        this.alertService.error(this.t('campaign.campaignForm.audienceFileTooManyRows', {
          max: String(this.AUDIENCE_FILE_MAX_ROWS),
          found: String(rawRows.length),
        }));
        this.clearAudienceFile(input);
        return;
      }

      const headers = Object.keys(rawRows[0] ?? {});
      this.audienceFileMappings = this.inferAudienceMappings(headers);
      this.audienceFileColumns = this.audienceColumnsOf(rawRows);

      this.audienceFileRawRows = rawRows;
      this.audienceSourceFile = file;
      this.audienceFileName = file.name;
      this.audienceFileSize = this.formatBytes(file.size);
      this.buildAudienceRows();
    } catch (error) {
      this.alertService.error(this.t('campaign.campaignForm.audienceFileParseError'));
      this.clearAudienceFile(input);
    } finally {
      this.loadingSpinnerSubject.next(false);
    }
  }

  /** Parses the chosen file, or — for a list uploaded earlier — the stored CSV at its URL. */
  private parseAudienceCsv(source: File | string): Promise<Array<Record<string, any>>> {
    return new Promise((resolve, reject) => {
      Papa.parse(<any>source, {
        header: true,
        skipEmptyLines: true,
        encoding: 'UTF-8',
        download: typeof source === 'string',
        complete: (results: Papa.ParseResult<any>) => resolve((results.data ?? []).filter(r => !!r)),
        error: (error: any) => reject(error),
      });
    });
  }

  /** Reads the first worksheet, treating row 1 as the header. */
  private async parseAudienceXlsx(file: File): Promise<Array<Record<string, any>>> {
    const workbook = new Workbook();
    await workbook.xlsx.load(await file.arrayBuffer());

    const sheet = workbook.worksheets[0];
    if (!sheet) return [];

    const headers: Array<string> = [];
    sheet.getRow(1).eachCell((cell, col) => headers[col - 1] = String(cell.value ?? '').trim());

    const rows: Array<Record<string, any>> = [];
    sheet.eachRow((row, rowNumber) => {
      if (rowNumber === 1) return;
      const parsed: Record<string, any> = {};
      let hasValue = false;
      headers.forEach((header, index) => {
        if (!header) return;
        const value = row.getCell(index + 1).value;
        // exceljs returns objects for formulas, hyperlinks and rich text
        const plain = (value && typeof value === 'object')
          ? ((value as any).text ?? (value as any).result ?? (value as any).hyperlink ?? '')
          : value;
        parsed[header] = plain ?? '';
        if (String(parsed[header]).trim()) hasValue = true;
      });
      if (hasValue) rows.push(parsed);
    });

    return rows;
  }

  /** Each column with its first filled value, for the mapping table. */
  private audienceColumnsOf(rows: Array<Record<string, any>>): Array<{ csvColumn: string; sample: string }> {
    return Object.keys(rows[0] ?? {}).map(csvColumn => {
      const filled = rows.find(row => String(row[csvColumn] ?? '').trim());
      return { csvColumn, sample: String(filled?.[csvColumn] ?? '').trim() };
    });
  }

  /** Same header regexes the contact importer already uses. */
  private inferAudienceMappings(headers: Array<string>): { phone?: string; name?: string; email?: string } {
    const mappings: { phone?: string; name?: string; email?: string } = {};

    for (const header of headers) {
      const normalized = (header || '').toLowerCase().trim();
      if (!mappings.phone && /(telefone|celular|phone|whats|zap|numero|número)/.test(normalized)) {
        mappings.phone = header;
      } else if (!mappings.name && /(nome|name|first\s*name|nome\s*completo)/.test(normalized)) {
        mappings.name = header;
      } else if (!mappings.email && /(email|e-mail)/.test(normalized)) {
        mappings.email = header;
      }
    }

    return mappings;
  }

  /**
   * Applies the mapping and the platform's phone rule to build the preview.
   * The server re-runs the same rule when it stages the audience, so what is
   * counted here is what will be dispatched.
   */
  buildAudienceRows(): void {
    const { phone: colPhone, name: colName, email: colEmail } = this.audienceFileMappings;

    const cell = (row: Record<string, any>, column?: string): string =>
      column ? String(row[column] ?? '').trim() : '';

    this.audienceFileRows = (this.audienceFileRawRows ?? []).map((row, index) => {
      const phone = sanitizePhone(cell(row, colPhone), this.audienceApplyCountryPrefix);
      const name = cell(row, colName);

      return {
        name,
        phone,
        email: cell(row, colEmail),
        index,
        hasError: !isValidPhone(phone) || !this.reachableByChannel(phone),
      };
    });

    // Counted here, once, and not in a getter: the list can hold 50,000 rows and
    // a getter would re-scan all of them on every change-detection pass.
    const errored = this.audienceFileRows.filter(r => r.hasError);
    this.audienceFileTotalRows = this.audienceFileRows.length;
    this.audienceFileValidRows = this.audienceFileTotalRows - errored.length;
    this.audienceFileMissingCountryCodeRows = errored.filter(r => isValidPhone(r.phone)).length;
    this.audienceFileInvalidPhoneRows = errored.length - this.audienceFileMissingCountryCodeRows;
    this.calculateAudienceAndLeads();
  }

  /**
   * Whether the chosen channel can reach this number.
   *
   * RCS delivers to Brazilian numbers only, which is exactly how the server
   * counts the audience and stages the dispatch. Without this the preview called
   * a number written without the country code valid, the summary then excluded
   * every one of them, and the step blocked while the list read "3 of 3 valid".
   */
  private reachableByChannel(phone: string): boolean {
    return !this.isSelectedChannelRcsAPI() || isBrazilianPhone(phone);
  }

  /** Why a row will not be sent — the country-code case names the toggle that fixes it. */
  audienceRowErrorKey(row: CampaignAudienceRow): string {
    return isValidPhone(row.phone)
      ? 'campaign.campaignForm.audienceFileMissingCountryCode'
      : 'campaign.campaignForm.audienceFileInvalidPhone';
  }

  get audienceFilePreviewRows(): Array<CampaignAudienceRow> {
    const rows = this.audienceShowOnlyErrors
      ? this.audienceFileRows.filter(r => r.hasError)
      : this.audienceFileRows;
    return rows.slice(0, this.AUDIENCE_PREVIEW_ROWS);
  }

  toggleAudienceErrorsOnly(): void {
    this.audienceShowOnlyErrors = !this.audienceShowOnlyErrors;
  }

  async onToggleAudienceCountryPrefix(): Promise<void> {
    this.audienceApplyCountryPrefix = !this.audienceApplyCountryPrefix;

    // The toggle changes the numbers that will actually be dispatched, so the
    // stored list has to be rewritten with them. Recomputing only the preview
    // left the server reading the original spelling: on RCS an audience without
    // the country code stayed at zero reachable and the step never unblocked,
    // while the screen showed every number already carrying the 55.
    await this.rebuildAudienceFile();
  }

  audienceFieldOf(csvColumn: string): string {
    return Object.entries(this.audienceFileMappings).find(([, mapped]) => mapped === csvColumn)?.[0] ?? 'ignore';
  }

  /** One column per field and one field per column — the rule the other importers apply. */
  onAudienceMappingChange(csvColumn: string, field: 'phone' | 'name' | 'email' | 'ignore'): void {
    const mappings = Object.fromEntries(
      Object.entries(this.audienceFileMappings).filter(([, mapped]) => mapped !== csvColumn),
    );
    if (field !== 'ignore') mappings[field] = csvColumn;
    this.audienceFileMappings = mappings;
    // The stored list no longer matches the mapping; confirming it writes a new one.
    this.formGroup.get('audienceFile').setValue(null);
    this.buildAudienceRows();
  }

  async confirmAudienceMapping(): Promise<void> {
    if (!this.formGroup.get('audienceFile').value) {
      await this.rebuildAudienceFile();
    }
    this.audienceFileStep = 2;
  }

  private async rebuildAudienceFile(): Promise<void> {
    this.buildAudienceRows();
    this.loadingSpinnerSubject.next(true);
    try {
      await this.uploadAudienceFile();
    } catch {
      this.alertService.error(this.t('campaign.campaignForm.audienceFileUploadError'));
    } finally {
      this.loadingSpinnerSubject.next(false);
    }
  }

  /**
   * Uploads the normalized CSV and records the descriptor. Nothing here creates
   * contacts or an import job — the campaign is the only consumer of this list.
   *
   * Runs when the mapping is confirmed and on every country-prefix change: the
   * descriptor is what the server counts and dispatches, so a preview it no
   * longer matches would promise an audience the dispatch does not have. The
   * source file is uploaded once — only the normalized CSV is rewritten, and the
   * superseded descriptor expires on its own TTL.
   *
   * A list picked from the picker has no File behind it: it is rebuilt from the
   * stored descriptor, which already points at the same source file.
   */
  private async uploadAudienceFile(): Promise<void> {
    const file = this.audienceSourceFile;
    const stored = this.audienceFile;
    const validRows = this.audienceFileRows.filter(r => !r.hasError);

    // Nothing to build from, or no row survives the rules: there is nothing to
    // persist, and the descriptor already stored no longer describes what the
    // preview shows — keeping it selected would let the step advance on an
    // audience nobody can see.
    if ((!file && !stored?.sourceUrl) || !validRows.length) {
      this.formGroup.get('audienceFile')?.setValue(null);
      return;
    }

    const stamp = new Date().getTime();
    const extension = (file?.name.split('.').pop() ?? '').toLowerCase();

    const csv = Papa.unparse(
      validRows.map(r => ({ telefone: r.phone, nome: r.name, email: r.email })),
      { header: true },
    );
    const normalizedFile = new File([csv], `${stamp}-normalized.csv`, { type: 'text/csv' });

    const sourceUrl = stored?.sourceUrl
      ?? (await this.firebaseService.uploadFile(file, `${stamp}-${file.name}`, UploadTypeEnum.CAMPAIGN_AUDIENCE)).url;
    const normalized = await this.firebaseService.uploadFile(normalizedFile, normalizedFile.name, UploadTypeEnum.CAMPAIGN_AUDIENCE);

    const audienceFile = <Partial<CampaignAudienceFile>>{
      filename: file?.name ?? stored.filename,
      contentType: file
        ? (extension === 'csv' ? 'text/csv' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet')
        : stored.contentType,
      sizeBytes: file?.size ?? stored.sizeBytes,
      sourceUrl,
      normalizedUrl: normalized.url,
      // the normalized CSV always carries these three headers
      mappings: { phone: 'telefone', name: 'nome', email: 'email' },
      totalRows: this.audienceFileTotalRows,
      validRows: this.audienceFileValidRows,
    };

    this.campaignAudienceFileService.create(audienceFile as CampaignAudienceFile)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (created: CampaignAudienceFile) => {
          this.audienceFile = created;
          this.formGroup.get('audienceFile').setValue(created._id);
          // The picker has to learn about the list just derived, or its select
          // would sit blank on an id missing from its options.
          if (this.isAudienceSourceFileExisting()) {
            this.loadAvailableAudienceFiles();
          }
        },
        error: () => this.alertService.error(this.t('campaign.campaignForm.audienceFileUploadError')),
      });
  }

  clearAudienceFile(input?: HTMLInputElement): void {
    this.audienceFile = null;
    this.audienceSourceFile = null;
    this.audienceFileRows = [];
    this.audienceFileRawRows = [];
    this.audienceFileMappings = {};
    this.audienceFileColumns = [];
    this.audienceFileStep = 0;
    this.audienceFileName = '';
    this.audienceFileSize = '';
    this.audienceFileTotalRows = 0;
    this.audienceFileValidRows = 0;
    this.audienceFileMissingCountryCodeRows = 0;
    this.audienceFileInvalidPhoneRows = 0;
    this.audienceShowOnlyErrors = false;
    this.audienceFilePreviewLoading = false;
    this.audienceFilePreviewFailed = false;
    this.formGroup?.get('audienceFile')?.setValue(null);
    if (input) input.value = '';
    if (this.inputAudienceFile?.nativeElement) this.inputAudienceFile.nativeElement.value = '';
  }

  private formatBytes(bytes: number): string {
    const KB = 1024, MB = KB * 1024;
    if (bytes >= MB) return `${(bytes / MB).toFixed(2)} MB`;
    if (bytes >= KB) return `${(bytes / KB).toFixed(2)} KB`;
    return `${bytes} B`;
  }

  isExecutionTypeImediate(): boolean {
    return this.formGroup.get('executionType').value === CampaignExecutionTypeEnum.IMEDIATE;
  }

  isExecutionTypeScheduled(): boolean {
    return this.formGroup.get('executionType').value === CampaignExecutionTypeEnum.SCHEDULED;
  }

  isSelectedChannelCloudAPI(): boolean {
    return this.selectedChannel?.type === ChannelTypeEnum.CLOUD_API;
  }

  isSelectedChannelZAPI(): boolean {
    return this.selectedChannel?.type === ChannelTypeEnum.Z_API;
  }

  isSelectedChannelRcsAPI(): boolean {
    return this.selectedChannel?.type === ChannelTypeEnum.RCS_API;
  }

  private loadAllChannels() {
    this.channelService.getList().subscribe(channels => {
      if (channels) {
        this.allChannel = channels.filter((channel) => this.dispatchesCampaign(channel));
        // Cópia, não a mesma referência: `fetchFilterChannel` dá push em
        // `channels` ao reinserir o canal já escolhido, e isso não pode vazar
        // para a lista completa.
        this.channels = [...this.allChannel];
      }
    })
  }

  /**
   * Se o canal pode ser usado num disparo.
   *
   * Instagram e Messenger nunca suportaram envio em massa. VoIP transporta voz
   * e Webchat é um widget de sessão iniciada pelo visitante — nenhum dos dois
   * tem para onde mandar uma campanha, então nem entram na escolha.
   */
  private dispatchesCampaign(channel: Channel): boolean {
    return !CampaignFormComponent.CAMPAIGN_INELIGIBLE_CHANNELS.includes(channel?.type);
  }

  /**
   * Um canal desconectado não entrega nada. Continua na lista, com o crachá
   * vermelho explicando o porquê, mas não pode ser escolhido — deixar escolher
   * só adiaria o erro para o disparo.
   */
  isChannelSelectable(channel: Channel): boolean {
    return !!channel?.connected;
  }

  private fetchFilterChannel(): void {
    this.filterControlChannel.valueChanges.pipe(
      debounceTime(300),
    ).subscribe((value: string) => {
      const currentValue = this.formGroup.get('channel')?.value || null;

      this.channels = this.allChannel.filter(channel =>
        channel.name.toLowerCase().includes(value.toLowerCase()) &&
        channel._id !== currentValue
      );

      const existingChannel = this.allChannel.find(channel => channel._id === currentValue);
      if (existingChannel) {
        this.channels.push(existingChannel);
      }
    });
  }

  onSelectChannel(channel: any): void {
    this.selectedChannel = this.channels.find(c => c._id === channel);
    if (this.isSelectedChannelCloudAPI()) {
      this.formGroup.get('cloudAPIDataGroup.template').setValidators(Validators.required);
    } else {
      this.formGroup.get('cloudAPIDataGroup.template').setValidators(null);
    }
    this.formGroup.get('cloudAPIDataGroup.template').setValue(null);
    this.formGroup.get('cloudAPIDataGroup.template').updateValueAndValidity();
    // The control alone is not the state the price reads — `selectedTemplate`
    // is. Leaving it behind would keep quoting the old channel's template.
    this.selectedTemplate = null;
    this.resetCloudApiTemplatePrice();

    if (this.isSelectedChannelRcsAPI()) {
      if (!this.getRCSFormGroup.get('type')?.value) {
        this.getRCSFormGroup.get('type').setValue(RCSMessageTypeEnum.TEXT);
      }
      // Carimba o tipo do agente a partir do canal. O campo continua sendo
      // persistido em `RCSData`, mas agora descreve o agente que de fato vai
      // enviar, em vez de repetir o que o operador marcou num radio.
      this.getRCSFormGroup.get('agentType').setValue(
        this.isRcsAgentConversational() ? 'CONVERSATIONAL' : 'NON_CONVERSATIONAL',
      );
    } else {
      this.getRCSFormGroup.get('type').setValue('');
    }
    applyRcsTypeValidators(this.getRCSFormGroup, { clear: true });

    // Which numbers are reachable depends on the channel (see `reachableByChannel`),
    // so a list already on screen has to be re-checked against the new one.
    if (this.audienceFileRows.length) {
      this.buildAudienceRows();
    }
  }

  onFocusChannel(): void {
    this.channels = this.allChannel;
  }

  selectChannelCard(channel: Channel): void {
    if (!this.isChannelSelectable(channel)) return;
    this.onSelectChannel(channel._id);

    this.formGroup.get('channel')?.setValue(channel._id);
    this.formGroup.get('channel')?.markAsTouched();
  }

  isChannelSelected(channel: Channel): boolean {
    return this.formGroup.get('channel')?.value === channel._id;
  }

  channelTypeKey(channel: Channel): string {
    const chaves: { [type: string]: string } = {
      [ChannelTypeEnum.CLOUD_API]: 'campaign.campaignForm.channelKindCloudApi',
      [ChannelTypeEnum.Z_API]: 'campaign.campaignForm.channelKindZapi',
      [ChannelTypeEnum.RCS_API]: 'campaign.campaignForm.channelKindRcs',
    };
    return chaves[channel?.type] ?? '';
  }

  channelBadgeKey(channel: Channel): string {
    if (!channel?.connected) {
      return 'campaign.campaignForm.channelDisconnected';
    }

    const chaves: { [score: string]: string } = {
      GREEN: 'campaign.campaignForm.channelQualityHigh',
      YELLOW: 'campaign.campaignForm.channelQualityMedium',
      RED: 'campaign.campaignForm.channelQualityLow',
    };
    return chaves[this.channelQualityRating(channel)] ?? 'campaign.campaignForm.channelConnected';
  }

  channelBadgeClass(channel: Channel): string {
    if (!channel?.connected) {
      return 'is-err';
    }

    switch (this.channelQualityRating(channel)) {
      case 'YELLOW': return 'is-warn';
      case 'RED': return 'is-err';
      default: return 'is-ok';
    }
  }

  private channelQualityRating(channel: Channel): string {
    return String(channel?.metadata?.phoneNumberInfo?.quality_rating ?? '').toUpperCase();
  }

  /**
   * A chave da nota de qualidade, ou `null` quando o provedor não informa.
   *
   * Só a Cloud API devolve `quality_rating`; em Z-API e RCS a linha inteira
   * não aparece, em vez de mostrar travessão para um dado que não existe
   * naquele canal.
   */
  channelQualityKey(channel: Channel): string | null {
    const chaves: { [score: string]: string } = {
      GREEN: 'campaign.campaignForm.channelQualityHigh',
      YELLOW: 'campaign.campaignForm.channelQualityMedium',
      RED: 'campaign.campaignForm.channelQualityLow',
    };
    return chaves[this.channelQualityRating(channel)] ?? null;
  }

  channelStateKey(channel: Channel): string {
    return channel?.connected
      ? 'campaign.campaignForm.channelConnected'
      : 'campaign.campaignForm.channelDisconnected';
  }
  previewOpen = true;
  summaryOpen = true;
  /**
   * Itens da lista de canais abertos, por `_id`.
   *
   * Estado próprio, e não um `open` no objeto do canal: `channels` é refeito a
   * cada busca no filtro, e um campo no objeto se perderia junto. Vários podem
   * ficar abertos ao mesmo tempo — a seta é independente da seleção, então
   * comparar o comportamento de dois agentes RCS é um caso legítimo.
   */
  private readonly expandedChannels = new Set<string>();

  isChannelExpanded(channel: Channel): boolean {
    return this.expandedChannels.has(channel?._id);
  }

  toggleChannelDetail(channel: Channel): void {
    const id = channel?._id;
    if (!id) {
      return;
    }
    if (this.expandedChannels.has(id)) {
      this.expandedChannels.delete(id);
      return;
    }
    this.expandedChannels.add(id);
  }

  private walletSummaries: Array<WalletSummary> = [];
  /**
   * The wallets have answered, or failed to. Until then a wallet-billed Cloud
   * API dispatch has no table to price from — a figure on its way, not a missing
   * one, and it must not read as "unavailable" (see `summaryPriceLoading`).
   */
  private walletSummariesLoaded = false;

  get balance(): number | null {
    const wallet = this.walletForChannel;
    return wallet ? wallet.balance ?? 0 : null;
  }

  get summaryReachCount(): number | null {
    return this.audienceSummary?.totalToSend ?? null;
  }

  /**
   * The wallet this channel consumes from: RCS for an RCS channel, WhatsApp for
   * a credit-line Cloud API channel, none for any other channel. An ordinary
   * Cloud API channel is priced from the template it sends (see
   * `cloudApiUnitPrice`), never from here.
   */
  private get walletForChannel(): WalletSummary | null {
    const walletType = walletTypeForChannel(this.selectedChannel);
    if (!walletType) return null;
    return this.walletSummaries.find((summary) => summary.type === walletType) ?? null;
  }

  /**
   * The selected channel is a Cloud API channel billed by the WhatsApp wallet —
   * a credit-line channel (SPEC-2026-10-whatsapp-wallet-credit-line-channels
   * RF-32).
   *
   * THE switch between the two ways a Cloud API dispatch is quoted, read by
   * every getter below. True: the price is ours, in reais, taken from the
   * wallet's price table for the template's category, with the balance and the
   * balance after shown as they are for RCS. False, an ordinary Cloud API
   * channel: Meta bills the tenant directly, so the only figure there is what
   * Meta reports the template has been costing, in the WABA's own currency, and
   * no balance is involved (RF-34).
   *
   * Asked of the CHANNEL, through the resolver every screen shares, and not of
   * the wallet rows: the answer is known the moment the channel is picked, so
   * Meta's estimate is never requested for a channel the wallet bills — not even
   * while the wallets are still loading, nor when they failed to load.
   */
  get cloudApiBilledByWallet(): boolean {
    return this.isSelectedChannelCloudAPI()
      && walletTypeForChannel(this.selectedChannel) === WalletTypeEnum.WHATSAPP;
  }

  /**
   * Price of one message for this dispatch.
   *
   * RCS bills EVERY dispatch at the SINGLE rate, on a conversational agent as
   * much as on a campaign-only one: the higher session rate is charged only for
   * the recipients who actually answer, and it replaces this one rather than
   * adding to it (see `summarySessionPrice`). Cloud API bills per template: on a
   * credit-line channel the wallet's price for that template's category, on an
   * ordinary one what Meta actually charged for that template.
   */
  get summaryPricePerMessage(): number | null {
    if (this.cloudApiBilledByWallet) return this.walletTemplatePrice;
    // No currency, no price. Suppressing HERE is what makes every downstream
    // reader inherit it untouched — summaryShowsPricePerMessage, the estimated
    // cost, `cost` and the modal all derive from this getter.
    if (this.isSelectedChannelCloudAPI()) {
      return this.cloudApiCurrency ? this.cloudApiUnitPrice : null;
    }
    const priceTable = this.walletForChannel?.priceTable;
    if (!priceTable) return null;
    return priceTable[WalletBillingCategoryEnum.SINGLE] ?? null;
  }

  /**
   * What the WhatsApp wallet charges for one message of the selected template:
   * the row of its price table for the template's category — the lookup the
   * ledger makes (`WalletTypeUtil.priceOf` in trendmulti-core), so the quote and
   * the charge read the same row.
   *
   * `null` is "no figure": no template picked yet, no wallet in hand, a category
   * a template cannot carry, or a category nobody priced. The last two are still
   * sent, posted at zero and flagged unpriced — which is why the rail then shows
   * a label and never "R$ 0,00" (RF-33). A price of zero IS a price, as it is for
   * the ledger.
   */
  private get walletTemplatePrice(): number | null {
    const category = walletBillingCategoryFor(WalletTypeEnum.WHATSAPP, {
      isTemplate: true,
      templateCategory: this.selectedTemplate?.category,
    });
    const price = category ? this.walletForChannel?.priceTable?.[category] : null;
    return typeof price === 'number' && Number.isFinite(price) && price >= 0 ? price : null;
  }

  /**
   * The rate a message is RE-CHARGED at once the customer replies and opens a 24h
   * session (SPEC-2026-08-typed-wallets RF-52). It applies per recipient and only
   * to those who answer, so it cannot be multiplied out over the audience — it is
   * shown as a hint beside the per-message price instead.
   */
  get summarySessionPrice(): number | null {
    // RCS only: no other wallet re-prices a send when the customer replies.
    if (!this.isSelectedChannelRcsAPI()) return null;
    const priceTable = this.walletForChannel?.priceTable;
    if (!priceTable) return null;
    return priceTable[WalletBillingCategoryEnum.CONVERSATIONAL] ?? null;
  }

  /**
   * Currency the quoted price is in. What a wallet bills — RCS, and Cloud API on
   * a credit-line channel — is billed by us, in reais. An ordinary Cloud API
   * channel is billed by Meta in the WABA's own currency — which is why some
   * tenants see dollars — so it comes back with the template's cost data.
   */
  get summaryPriceCurrency(): string {
    if (this.cloudApiBilledByWallet) return 'BRL';
    // Stays `string` on purpose. The 'BRL' fallback is INERT: when the currency
    // is unknown the price was already suppressed above, so no pipe reading this
    // is ever evaluated. `summaryCurrencyUnknown` carries the real meaning.
    return (this.isSelectedChannelCloudAPI() ? this.cloudApiCurrency : 'BRL') ?? 'BRL';
  }

  /**
   * Decimals of the per-message price. A wallet tariff can carry up to four
   * (R$ 0,0350 for a WhatsApp utility template); rounded to cents it would read
   * R$ 0,04 and disagree with the total below it. The provider's estimate on an
   * ordinary Cloud API channel keeps the two decimals it has always shown.
   */
  get summaryPriceDigits(): string {
    return this.isSelectedChannelCloudAPI() && !this.cloudApiBilledByWallet ? '1.2-2' : '1.2-4';
  }

  /** Cloud API with no template picked: there is nothing to price yet. */
  get summaryAwaitingTemplate(): boolean {
    return this.isSelectedChannelCloudAPI() && !this.selectedTemplate;
  }

  /**
   * The figure for the selected template is on its way — a spinner, not a
   * label. On an ordinary Cloud API channel that is Meta's answer about the
   * template; on a wallet-billed one it is the wallets themselves, which can
   * still be in flight when a saved dispatch is reopened.
   */
  get summaryPriceLoading(): boolean {
    if (this.cloudApiBilledByWallet) return !!this.selectedTemplate && !this.walletSummariesLoaded;
    return this.cloudApiPriceLoading;
  }

  private resetCloudApiTemplatePrice(): void {
    this.cloudApiUnitPrice = null;
    this.cloudApiCurrency = null;
    this.cloudApiPriceLoading = false;
    this.cloudApiPriceFailed = false;
  }

  /**
   * Ask Meta what this template has been costing.
   *
   * `cost_per_delivered` over the last 30 days, for this template id alone. It
   * is historical by nature — Meta exposes no forward rate for an unsent
   * template — so a brand-new template legitimately comes back empty and the
   * wizard says so rather than quoting zero. `currency` is the WABA's billing
   * currency, which is why the same screen shows R$ for one tenant and $ for
   * another.
   *
   * Never asked for a wallet-billed channel: the price there is the wallet's,
   * and Meta's figure would be what the credit line is billed, not what this
   * tenant pays. The reset below still runs first, so picking a template on such
   * a channel leaves no spinner, price or failure behind from a previous one.
   */
  private loadCloudApiTemplatePrice(): void {
    this.resetCloudApiTemplatePrice();

    const idChannel = this.selectedChannel?._id;
    const idTemplate = this.selectedTemplate?.id;
    if (!this.isSelectedChannelCloudAPI() || !idChannel || !idTemplate) return;
    if (this.cloudApiBilledByWallet) return;

    const end = new Date();
    const start = new Date(end);
    start.setUTCDate(end.getUTCDate() - 30);

    this.cloudApiPriceLoading = true;
    this.insightsService.getInsightsTemplates(idChannel, <any>{
      start: Math.floor(start.getTime() / 1000),
      end: Math.floor(end.getTime() / 1000),
      filteringParameter: {
        metric_types: ['SENT', 'DELIVERED', 'COST'],
        template_ids: [idTemplate],
      },
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data: any) => {
          // An answer for a channel left behind: the operator moved to a
          // wallet-billed one while Meta was still being asked. Nothing of it may
          // land on a dispatch Meta's figure does not price.
          if (this.cloudApiBilledByWallet) return;
          // Identity guard BEFORE clearing the spinner. The Meta client retries
          // up to five times with backoff (~15s), so a stale answer can land
          // while a newer request is still in flight — clearing the flag first
          // would hide a spinner that is still legitimately spinning.
          if (this.selectedTemplate?.id !== idTemplate) return;
          this.cloudApiPriceLoading = false;

          // The currency comes from OUR channel record, not from Meta, so it is
          // valid even when the analytics call failed. Read it before bailing
          // out, or an outage would be reported as a misconfigured account and
          // send the operator to re-save a channel that is perfectly fine.
          this.cloudApiCurrency = data?.currency ?? null;

          // Meta failures arrive as HTTP 200 with an `error` envelope, so the
          // error callback below never sees them.
          if (data?.error) {
            this.cloudApiPriceFailed = true;
            return;
          }

          this.cloudApiUnitPrice = this.costPerDelivered(data?.data?.[0]?.data_points ?? []);
        },
        error: () => {
          // Same as above: a late failure must not mark a wallet-billed dispatch.
          if (this.cloudApiBilledByWallet) return;
          this.cloudApiPriceLoading = false;
          this.cloudApiPriceFailed = true;
          // Leave the price null. An analytics outage must not turn into a
          // confident zero on a screen the operator is about to confirm.
        },
      });
  }

  /**
   * Mean `cost_per_delivered` across the returned points.
   *
   * Meta reports one point per granularity bucket, and the rate can genuinely
   * move within the window (category reclassification, rate-card changes), so a
   * single point would be arbitrary. Points with no cost entry are skipped
   * rather than counted as zero — a day with no sends is missing data, not a
   * free day, and averaging it in would drag the quote down.
   */
  private costPerDelivered(points: Array<any>): number | null {
    let cost = 0;
    let delivered = 0;

    for (const point of points) {
      const rate = (point?.cost ?? []).find((item: any) => item?.type === 'cost_per_delivered')?.value;
      const sent = point?.delivered ?? point?.sent;
      if (typeof rate !== 'number' || !Number.isFinite(rate) || rate <= 0) continue;
      if (typeof sent !== 'number' || !Number.isFinite(sent) || sent <= 0) continue;
      cost += rate * sent;
      delivered += sent;
    }

    return delivered > 0 ? cost / delivered : null;
  }

  /**
   * Why there is no figure — the i18n key of the reason. The causes are
   * different and the operator can act on only some of them: a wallet category
   * with no tariff filled in is ours to fix, a WABA without a currency needs the
   * channel re-saved, and a template Meta has no recent cost for is simply not
   * billable data yet.
   */
  get summaryUnpricedLabel(): string {
    if (this.cloudApiBilledByWallet) {
      // With the wallet in hand, no figure means its table has no price for the
      // template's category: the send goes out and is posted at zero, so the
      // rail uses the statement's own word for that entry (RF-33). Without the
      // wallet — its summary failed to load — the price is unknown, not absent.
      return this.walletForChannel ? 'wallet.ledger.unpriced' : 'campaign.campaignForm.priceUnavailable';
    }
    if (this.summaryCurrencyUnknown) return 'campaign.campaignForm.priceCurrencyUnknown';
    if (this.cloudApiPriceFailed) return 'campaign.campaignForm.priceUnavailable';
    return 'campaign.campaignForm.priceNoHistory';
  }

  /**
   * The WABA never reported a billing currency. Its own state because the fix is
   * different from every other one: nothing about the template or the audience
   * will help — the channel record has to be re-saved, which re-syncs it.
   */
  get summaryCurrencyUnknown(): boolean {
    // The wallet bills in reais whatever the WABA reports, or fails to report.
    if (this.cloudApiBilledByWallet) return false;
    return this.isSelectedChannelCloudAPI()
      && !this.cloudApiPriceLoading
      && !!this.selectedTemplate
      && !this.cloudApiCurrency;
  }

  /** We know what to price and still have no figure — say so instead of "R$ 0,00". */
  get summaryUnpriced(): boolean {
    // CLOUD API ONLY, deliberately — both ways it is priced, the wallet's and
    // Meta's. RCS keeps exactly what it had: with an empty priceTable the price
    // line stays hidden and the estimated cost reads R$ 0,00 — which also
    // happens to match what the ledger posts for an unpriced category.
    // Extending this state to RCS would change a screen that was not in scope
    // and would disagree with the balance line beside it.
    if (!this.isSelectedChannelCloudAPI()) return false;
    if (this.summaryPriceLoading || this.summaryAwaitingTemplate) return false;
    // The GETTER, not the field: suppression by unknown currency — or by a
    // wallet category without a price — has to land in "unpriced" too, otherwise
    // the estimated-cost line falls through and prints a confident zero.
    return this.summaryPricePerMessage === null;
  }

  get summaryShowsPricePerMessage(): boolean {
    return this.summaryPricePerMessage !== null;
  }

  get summaryEstimatedCost(): number {
    return (this.audienceSummary?.totalToSend ?? 0) * (this.summaryPricePerMessage ?? 0);
  }

  get summaryShowsBalance(): boolean {
    return this.walletForChannel !== null;
  }

  get summaryBalanceAfter(): number | null {
    const balance = this.balance;
    return balance === null ? null : balance - this.summaryEstimatedCost;
  }

  get summaryHasBalance(): boolean {
    const after = this.summaryBalanceAfter;
    return after === null || after >= 0;
  }

  get disconnectedChannels(): Array<Channel> {
    return (this.channels ?? []).filter((channel) => !channel.connected);
  }

  get disconnectedChannelNames(): string {
    return this.disconnectedChannels.map((channel) => channel.name).join(', ');
  }

  private loadWalletSummaries(): void {
    this.walletService.getSummary()
      .pipe(takeUntil(this.destroy$), catchError(() => of<Array<WalletSummary>>([])))
      .subscribe((summaries) => {
        this.walletSummaries = summaries ?? [];
        this.walletSummariesLoaded = true;
      });
  }

  private loadAllTags() {
    this.tagService.getList().subscribe(tags => {
      if (tags) {
        this.tags = tags;
        this.allTags = tags;
        this.checkTags();
      }
    });
  }

  private fetchFilterTags(): void {
    this.filterControlTag.valueChanges.pipe(
      debounceTime(300)
    ).subscribe((value: string) => {
      const currentValue = this.formGroup.get('tags')?.value || [];
      const ignoredValue = this.formGroup.get('ignoredTags')?.value || [];

      this.allTags = this.tags.filter(tag =>
        tag.title.toLowerCase().includes(value.toLowerCase()) &&
        !currentValue.includes(tag._id) &&
        !ignoredValue.includes(tag._id)
      );

      currentValue.forEach(tagId => {
        const existingTags = this.tags.find(tag => tag._id === tagId);
        if (existingTags) {
          this.allTags.push(existingTags);
        }
      });

      ignoredValue.forEach(tagId => {
        const existingTags = this.tags.find(tag => tag._id === tagId);
        if (existingTags) {
          this.allTags.push(existingTags);
        }
      });
    });
  }

  onFocusTags(): void {
    this.allTags = this.tags;
  }

  private loadAllContactGroups() {
    this.contactGroupService.getByIndustry().subscribe(contactGroups => {
      if (contactGroups) {
        this.contactGroups = contactGroups;
        this.allGroups = contactGroups;
        this.checkContactGroups();
      }
    });
  }

  private fetchFilterGroups(): void {
    this.filterControlGroup.valueChanges.pipe(
      debounceTime(300)
    ).subscribe((value: string) => {
      const currentValue = this.formGroup.get('contactGroups')?.value || [];
      const ignoredValue = this.formGroup.get('ignoredContactGroups')?.value || [];

      this.allGroups = this.contactGroups.filter(group =>
        group.name.toLowerCase().includes(value.toLowerCase()) &&
        !currentValue.includes(group._id) &&
        !ignoredValue.includes(group._id)
      );


      currentValue.forEach(groupId => {
        const existingGroup = this.contactGroups.find(group => group._id === groupId);
        if (existingGroup) {
          this.allGroups.push(existingGroup);
        }
      });

      ignoredValue.forEach(groupId => {
        const existingGroup = this.contactGroups.find(group => group._id === groupId);
        if (existingGroup) {
          this.allGroups.push(existingGroup);
        }
      });
    });
  }

  onFocusGroup(): void {
    this.allGroups = this.contactGroups;
  }

  checkTags() {
    if (!this.tags || !this.campaign) return;
    this.allTags = this.tags.map(t => {
      return {
        ...t,
        checked: (this.campaign?.audienceTags?.findIndex(at => at.title === t.title) >= 0),
        ignored: (this.campaign?.ignoredAudienceTags?.findIndex(at => at._id === t._id) >= 0)
      }
    })
  }

  checkContactGroups() {
    if (!this.contactGroups || !this.campaign) return;
    this.allGroups = this.contactGroups.map(t => {
      return {
        ...t,
        checked: (this.campaign?.audienceContactGroup?.findIndex(at => at.name === t.name) >= 0),
        ignored: (this.campaign?.ignoredAudienceContactGroup?.findIndex(at => at._id === t._id) >= 0)
      }
    })
  }

  /**
   * Ask the server for the five lines describing the current selection.
   *
   * The counting that used to live in this component — one round trip per
   * audience mode, no de-duplication, and ignore-lists that only applied to
   * "all contacts" — is gone. The server is the only place that can compute
   * these lines correctly, because it is the same place that materialises the
   * audience; anything computed here could disagree with what gets dispatched.
   */
  calculateAudienceAndLeads(): void {
    // An audience mode that needs items, with none picked, is not a selection to
    // be summarised — it is the empty state. Asking the server would come back
    // with a legitimate all-zero summary, and the panel would then render four
    // honest-looking zeros where it should say nothing has been chosen yet.
    if (!this.hasAudienceSelection()) {
      this.audienceSummary = null;
      this.audienceSummaryLoading = false;
      this.audienceSummaryFailed = false;
      return;
    }

    this.audienceSelection$.next(this.buildAudienceSelection());
  }

  /** Whether the current audience mode has everything it needs to be counted. */
  private hasAudienceSelection(): boolean {
    switch (this.formGroup.get('audienceType')?.value) {
      case CampaignAudienceTypeEnum.ALL_LEADS:
        return true;
      case CampaignAudienceTypeEnum.TAGS:
        return !!this.formGroup.get('tags')?.value?.length;
      case CampaignAudienceTypeEnum.CONTACT_GROUP:
        return !!this.formGroup.get('contactGroups')?.value?.length;
      case CampaignAudienceTypeEnum.FILE:
        return !!this.formGroup.get('audienceFile')?.value;
      default:
        return false;
    }
  }

  /** The selection as the endpoint expects it. */
  private buildAudienceSelection(): AudienceSelection {
    return <AudienceSelection>{
      audienceType: this.formGroup.get('audienceType')?.value,
      tags: this.formGroup.get('tags')?.value ?? [],
      contactGroups: this.formGroup.get('contactGroups')?.value ?? [],
      audienceFile: this.formGroup.get('audienceFile')?.value ?? undefined,
      ignoredTags: this.formGroup.get('ignoredTags')?.value ?? [],
      ignoredContactGroups: this.formGroup.get('ignoredContactGroups')?.value ?? [],
      channel: this.selectedChannel?._id,
    };
  }

  /**
   * Debounce, de-duplicate and cancel.
   *
   * `debounceTime` turns a burst of clicks into one request; `distinctUntilChanged`
   * stops a re-selection of the same set from refetching; `switchMap` cancels a
   * request the operator has already moved past, so a slow answer for an old
   * selection can never land on top of a newer one.
   */
  private initAudienceSummaryPipeline(): void {
    this.audienceSelection$
      .pipe(
        debounceTime(300),
        distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
        tap(() => {
          this.audienceSummaryLoading = true;
          this.audienceSummaryFailed = false;
        }),
        switchMap((selection) =>
          this.campaignService.audienceSummary(selection).pipe(
            catchError(() => {
              this.audienceSummaryFailed = true;
              return of<AudienceSummary>(null);
            }),
          ),
        ),
        takeUntil(this.destroy$),
      )
      .subscribe((summary) => {
        this.audienceSummaryLoading = false;
        this.audienceSummary = summary;
      });

    // Only after the pipeline can receive: the subject does not replay, so a
    // selection emitted before this point would be dropped.
    this.watchAudienceSelectionChanges();

    // The default audience ("all contacts") is a complete selection that no
    // valueChanges will ever announce, so it has to be computed once here or the
    // panel opens empty on a screen that already has an audience.
    this.calculateAudienceAndLeads();
  }

  /**
   * Recompute whenever any part of the selection moves.
   *
   * Without this the panel only ever refreshed on entering the review step:
   * picking a tag changed the form control and nothing recalculated, so the
   * summary sat on its empty state while an audience was plainly selected. The
   * debounced pipeline existed but nothing fed it.
   *
   * Watching the controls rather than wiring each picker's `(selectionChange)`
   * also covers the paths that patch the form programmatically — loading an
   * existing campaign for edit, clearing a file, switching source.
   */
  private watchAudienceSelectionChanges(): void {
    const controls = [
      'audienceType',
      'tags',
      'contactGroups',
      'ignoredTags',
      'ignoredContactGroups',
      'audienceFile',
      'channel',
    ];

    for (const name of controls) {
      this.formGroup.get(name)?.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => this.calculateAudienceAndLeads());
    }
  }

  /** Sizes for every tag and group, for the pickers (RF-15). */
  private loadAudienceCounts(): void {
    this.contactService.audienceCounts()
      .pipe(takeUntil(this.destroy$), catchError(() => of<AudienceCounts>({ all: 0, tags: {}, groups: {} })))
      .subscribe((counts) => this.audienceCounts = counts ?? { all: 0, tags: {}, groups: {} });
  }

  /**
   * Recount one item at the moment it is selected.
   *
   * The cached size is allowed to lag for items nobody picked; the moment one
   * feeds the Selected base line it has to be current, and the operator sees a
   * small loader while it is.
   */
  recountAudienceItem(kind: 'tag' | 'group', id: string): void {
    if (!id) return;
    this.audienceCountsLoading.add(id);
    this.contactService.audienceCountItem(kind, id)
      .pipe(takeUntil(this.destroy$), catchError(() => of<number>(null)))
      .subscribe((count) => {
        this.audienceCountsLoading.delete(id);
        if (count === null) return;
        if (kind === 'tag') this.audienceCounts.tags[id] = count;
        else this.audienceCounts.groups[id] = count;
      });
  }

  /**
   * An item was picked or unpicked: recount whatever is now selected.
   *
   * Only the selected ones — the cached size is good enough for an item the
   * operator is merely looking at, and recounting all of them on every click is
   * exactly the per-item cost the map exists to avoid.
   */
  onAudienceItemsSelected(kind: 'tag' | 'group', event: { value: Array<string> }): void {
    for (const id of event?.value ?? []) {
      this.recountAudienceItem(kind, id);
    }
  }

  /** The size to show beside one item, or `null` while it is being recounted. */
  audienceItemCount(kind: 'tag' | 'group', id: string): number | null {
    if (this.audienceCountsLoading.has(id)) return null;
    const counts = kind === 'tag' ? this.audienceCounts.tags : this.audienceCounts.groups;
    return counts[id] ?? 0;
  }

  /** True once a selection exists but has produced no reachable recipient (RF-11c). */
  get audienceReachesNobody(): boolean {
    return !!this.audienceSummary
      && this.audienceSummary.selectedBase > 0
      && this.audienceSummary.totalToSend === 0;
  }

  /** The Origin line: the names of what was selected, comma separated (RF-01). */
  get summaryOriginText(): string {
    const names = this.audienceSummary?.originNames ?? [];
    if (names.length) return names.join(', ');
    if (this.formGroup?.get('audienceType')?.value === CampaignAudienceTypeEnum.ALL_LEADS) {
      return this.t('campaign.campaignForm.originBase');
    }
    return '';
  }

  onMessageTypeChangeZapi(): void {
    const messageType = this.getZAPIFormGroup.get('messageType')?.value;

    if (messageType === 'buttons') {
      this.getZAPIFormGroup.get('message')?.setValidators(null);
      this.getZAPIFormGroup.get('buttonsMessage')?.setValidators(Validators.required);

      this.getZAPIFormGroup.get('midia')?.setValue(null);
      this.getZAPIFormGroup.get('contentType')?.setValue(null);
      this.getZAPIFormGroup.get('type')?.setValue(null);
    } else {
      this.getZAPIFormGroup.get('buttonsMessage')?.setValidators(null);
      this.getZAPIFormGroup.get('message')?.setValidators(Validators.required);

      this.getZAPIFormGroup.get('buttonsTitle')?.setValue(null);
      this.getZAPIFormGroup.get('buttonsMessage')?.setValue(null);
      this.getZAPIFormGroup.get('buttonsFooter')?.setValue(null);
      const actions = this.getZAPIFormGroup.get('buttonActions') as FormArray;
      if (actions) {
        while (actions.length) {
          actions.removeAt(0);
        }
      }
    }

    this.getZAPIFormGroup.get('message')?.updateValueAndValidity();
    this.getZAPIFormGroup.get('buttonsMessage')?.updateValueAndValidity();
  }


  disableNextChannel(): boolean {
    return this.formGroup.get('channel')?.invalid || this.formGroup.get('name')?.invalid;
  }

  disableNextMessage(): boolean {
    if (this.isSelectedChannelZAPI()) {
      const messageType = this.getZAPIFormGroup.get('messageType')?.value;
      if (messageType === 'buttons') {
        if (!this.getZAPIFormGroup.get('buttonsMessage')?.value) {
          return true;
        }
        const actions = this.getZAPIFormGroup.get('buttonActions') as FormArray;
        if (!actions || actions.length === 0) {
          return true;
        }
        return this.getZAPIFormGroup.get('buttonsMessage')?.invalid || actions.invalid;
      }

      if (!!this.getZAPIFormGroup.get('message')?.value) {
        return this.getZAPIFormGroup.get('message')?.invalid;
      }
      return true;
    } else if (this.isSelectedChannelRcsAPI()) {
      return this.getRCSFormGroup.invalid;
    } else if (this.isSelectedChannelCloudAPI()) {
      return this.formGroup.get('cloudAPIDataGroup')?.invalid;
    }
    return true;
  }

  disableNextRecipient(): boolean {
    // A selection that survives none of the deductions reaches nobody. Letting
    // it through would burn a campaign record and a wallet hold on a dispatch
    // with no recipient, and the panel already says so in words.
    if (this.audienceReachesNobody) {
      return true;
    }
    if (this.isAudienceTypeAllLeads()) {
      return this.formGroup.get('audienceType')?.invalid;
    } else if (this.isAudienceTypeTags()) {
      return this.formGroup.get('audienceType')?.invalid || this.formGroup.get('tags')?.invalid;
    } else if (this.isAudienceTypeContactGroups()) {
      return this.formGroup.get('audienceType')?.invalid || this.formGroup.get('contactGroups')?.invalid;
    } else if (this.isAudienceTypeFile()) {
      // an uploaded list is only an audience once it has a persisted descriptor
      // AND at least one usable number — a file of 300 malformed rows is not one
      return !this.formGroup.get('audienceFile')?.value || this.audienceFileValidRows < 1;
    }
    return true;
  }

  disableNextScheduling(): boolean {
    if (this.isExecutionTypeScheduled()) {
      return this.formGroup.get('executionType')?.invalid || this.formGroup.get('scheduledDay')?.invalid || this.formGroup.get('scheduledHour')?.invalid;
    } else {
      return this.formGroup.get('executionType')?.invalid;
    }
  }

  /**
   * The message control the Z-API editor toolbar acts on. The RCS composer has a toolbar of its own
   * (RcsMessageModule).
   */
  private get messageControl(): AbstractControl | null {
    return this.getZAPIFormGroup?.get('message');
  }

  updateMessage() {
    const textarea = this.messageTextarea.nativeElement as HTMLTextAreaElement;
    // The toolbar writes straight into the textarea, which fires no input event:
    // without this the formatting showed on screen and never reached the model.
    // The view already holds the value, so it is not written back — that would
    // move the caret to the end after formatting a word mid-message.
    this.messageControl?.setValue(textarea.value, { emitModelToViewChange: false });
  }

  onPaste(event: ClipboardEvent) {
    event.preventDefault();
    const clipboardData = event.clipboardData || (window as any).clipboardData;
    let pastedData = clipboardData.getData('text/html') || clipboardData.getData('text/plain');

    pastedData = pastedData
      .replace(/<p[^>]*>/g, '\n')
      .replace(/<\/p>/g, '\n\n')
      .replace(/<li[^>]*>/g, '')
      .replace(/<\/li>/g, '\n')
      .replace(/<\/?html[^>]*>/g, '')
      .replace(/<\/?body[^>]*>/g, '')
      .replace(/<!--.*?-->/g, '')
      .replace(/<\/?br[^>]*>/g, '\n')
      .replace(/<\/?strong[^>]*>/g, '*')
      .replace(/<\/?b[^>]*>/g, '*')
      .replace(/<\/?em[^>]*>/g, '_')
      .replace(/<\/?i[^>]*>/g, '_')
      .replace(/<\/?u[^>]*>/g, '~$&~');

    pastedData = pastedData.replace(/\n{2,}/g, '\n\n').trim();

    const textarea = this.messageTextarea.nativeElement as HTMLTextAreaElement;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;

    textarea.value =
      textarea.value.substring(0, start) +
      pastedData +
      textarea.value.substring(end);

    textarea.setSelectionRange(start + pastedData.length, start + pastedData.length);
    textarea.focus();
    this.updateMessage();
  }

  applyFormat(format: string) {
    const textarea = this.messageTextarea.nativeElement as HTMLTextAreaElement;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = textarea.value.substring(start, end);

    if (!selectedText) return;

    let formattedText = '';

    switch (format) {
      case 'bold':
        formattedText = `*${selectedText}*`;
        break;
      case 'italic':
        formattedText = `_${selectedText}_`;
        break;
      // WhatsApp has no underline: `~text~` is strikethrough, which is what the
      // button has always produced — the label said otherwise.
      case 'strikethrough':
        formattedText = `~${selectedText}~`;
        break;
      default:
        return;
    }

    textarea.value =
      textarea.value.substring(0, start) +
      formattedText +
      textarea.value.substring(end);

    textarea.setSelectionRange(start + formattedText.length, start + formattedText.length);
    textarea.focus();
    this.updateMessage();
  }

  selectVarContactZAPI(): void {
    const currentValue = this.getZAPIFormGroup.get('message')?.value || '';
    this.getZAPIFormGroup.get('message')?.setValue(currentValue + '{{NOME}}');
  }

  addEmoji(event: any): void {
    const messageFg = this.messageControl;
    const currentText = messageFg?.value || '';

    messageFg?.setValue(currentText + event.emoji.native);
  }

  closedContainerEmoji(): void {
    this.showContainerEmoji = false;
    this.clickOutsideListener()
    this.clickOutsideListener = null
  }

  toogleContainerEmoji(): void {
    clearTimeout(this.clearTimeout);

    this.showContainerEmoji = !this.showContainerEmoji;

    this.clearTimeout = setTimeout(() => {
      this.addClickOutsideListener();
    })
  }

  addClickOutsideListener() {
    this.clickOutsideListener = this.renderer.listen('document', 'click', (event: MouseEvent) => {
      if (this.showContainerEmoji && this.emojiContainer) {
        const modalElement = this.emojiContainer.nativeElement as HTMLElement;
        const clickedInside = modalElement?.contains(event.target as Node);

        if (!clickedInside) {
          this.closedContainerEmoji();
        }
      }
    });
  }

  addZAPIFile() {
    this.inputFileZAPIFile.nativeElement.click();
  }

  addCloudAPIFile() {
    this.inputFileCloudAPIFile.nativeElement.click();
  }

  onImageZAPILoadError(): void {
    this.formGroup.patchValue({ 'zapiDataGroup.midia': null });
  }

  onImageCloudAPILoadError(): void {
    const headersArr = this.headerParams;
    if (headersArr && headersArr.length > 0) {
      headersArr.at(0).get('value')?.setValue(null);
    }
  }

  removeMedia(target: string): void {
    const dialogRef = this.dialog.open(ConfirmationComponent, {
      width: '600px',
    });

    dialogRef.afterClosed().subscribe(result => {
      if (Boolean(result) === true) {
        if (this.formGroup.get('zapiDataGroup.midia')?.value === target) {
          this.formGroup.get('zapiDataGroup.midia')?.setValue(null);
          this.formGroup.get('zapiDataGroup.contentType')?.setValue(null);
          this.formGroup.get('zapiDataGroup.type')?.setValue(null);
        } else {
          const headersArr = this.headerParams;
          if (headersArr && headersArr.length > 0) {
            const headerVal = headersArr.at(0).get('value')?.value;
            if (headerVal === target) {
              headersArr.at(0).get('value')?.setValue(null);
            }
          }
        }
      }
    });
  }

  async onSelectZAPIFile(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input?.files?.[0];

    if (!file) {
      return;
    }

    const fileTypeLimit = UtilHelper.getFileTypeLimit(file.type);
    if (fileTypeLimit && file.size > fileTypeLimit.maxSize) {
      this.openModalAlert(fileTypeLimit.alertTitle, fileTypeLimit.alertMessage);
      return;
    }

    this.file = file;
    const reader = new FileReader()
    reader.onload = async (e) => {
      this.loadingSpinnerSubject.next(true);
      const contents = e.target?.result as ArrayBuffer;
      const base64 = UtilHelper.arrayBufferToBase64(contents);
      const filename = new Date().getTime().toString();
      try {
        const snapshot = await this.firebaseService.uploadFile(this.file, filename, UploadTypeEnum.CAMPAING);
        this.formGroup.get('zapiDataGroup.midia').setValue(snapshot.url);
        this.formGroup.get('zapiDataGroup.contentType').setValue(file.type);
        this.formGroup.get('zapiDataGroup.type').setValue(UtilHelper.getValidTypeCampaignZAPI(file.type));
      } catch (error) {
        this.loadingSpinnerSubject.next(false);
        this.alertService.error(this.t('campaign.campaignForm.imageUploadFailed'));
      } finally {
        this.loadingSpinnerSubject.next(false);
        input.value = '';
      }
    }

    reader.readAsArrayBuffer(file);
  }

  async onSelectCloudAPIFile(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input?.files?.[0];

    if (!file) {
      return;
    }

    const fileTypeLimit = UtilHelper.getFileTypeLimit(file.type);
    if (fileTypeLimit && file.size > fileTypeLimit.maxSize) {
      this.openModalAlert(fileTypeLimit.alertTitle, fileTypeLimit.alertMessage);
      return;
    }

    this.file = file;
    const reader = new FileReader()
    reader.onload = async (e) => {
      this.loadingSpinnerSubject.next(true);
      const contents = e.target?.result as ArrayBuffer;
      const base64 = UtilHelper.arrayBufferToBase64(contents);
      const filename = new Date().getTime().toString();
      try {
        const snapshot = await this.firebaseService.uploadFile(this.file, filename, UploadTypeEnum.CAMPAING);
        const headersArr = this.headerParams;
        const headerGroup = headersArr.at(0) as FormGroup;
        headerGroup.get('value')?.setValue(snapshot.url);
      } catch (error) {
        this.loadingSpinnerSubject.next(false);
        this.alertService.error(this.t('campaign.campaignForm.imageUploadFailed'));
      } finally {
        this.loadingSpinnerSubject.next(false);
        input.value = '';
      }
    }

    reader.readAsArrayBuffer(file);
  }

  private validate() {
    const form = { ...this.formGroup.value };

    if (form.audienceType === CampaignAudienceTypeEnum.TAGS && !form.tags) {
      this.alertService.error(this.t('campaign.campaignForm.selectAtLeastOneTag'))
      return false;
    }

    if (form.audienceType === CampaignAudienceTypeEnum.CONTACT_GROUP && !form.contactGroups) {
      this.alertService.error(this.t('campaign.campaignForm.selectAtLeastOneContactGroup'))
      return false;
    }

    if (form.audienceType === CampaignAudienceTypeEnum.FILE && !form.audienceFile) {
      this.alertService.error(this.t('campaign.campaignForm.selectAudienceFile'))
      return false;
    }

    if (form.executionType === CampaignExecutionTypeEnum.SCHEDULED) {
      if (!form.scheduledDay) {
        this.alertService.error(this.t('campaign.campaignForm.informScheduledDate'))
        return false;
      }
      if (!form.scheduledHour) {
        this.alertService.error(this.t('campaign.campaignForm.informScheduledTime'))
        return false;
      }
    }
    return true;
  }

  async confirmSaveCampaign() {
    if (!this.formGroup.valid) {
      this.validateAllFormFields(this.formGroup);
      return;
    }

    if (!this.validate()) return;

    const campaign = this.prepareSaveCampaign();

    const isCloudAPI = this.selectedChannel.type === ChannelTypeEnum.CLOUD_API;
    const asksForConfirmation = isCloudAPI
      || this.selectedChannel.type === ChannelTypeEnum.Z_API
      || this.selectedChannel.type === ChannelTypeEnum.RCS_API;

    if (!asksForConfirmation) return;

    const ignoredTags = this.formGroup.get('ignoredTags')?.value ?? [];
    const ignoredContactGroups = this.formGroup.get('ignoredContactGroups')?.value ?? [];

    const dialogRef = this.dialog.open(CampaingSaveConfirmModalComponent, {
      data: {
        campaign,
        ignoredTags: this.tags?.filter((tag: Tag) => ignoredTags.includes(tag._id)) ?? [],
        ignoredContactGroups: this.contactGroups?.filter((group: ContactGroup) => ignoredContactGroups.includes(group._id)) ?? [],
        channel: this.selectedChannel,
        // Only Cloud API prices per template category; the other channels have no
        // template to read a category from.
        selectedTemplate: isCloudAPI ? this.selectedTemplate : undefined,
        // The modal counts nothing of its own any more. It is handed the rail's
        // figures so the last screen before confirming cannot contradict the panel
        // the operator just read — it used to run its own head-count, with no
        // de-duplication and with the ignore-lists discarded.
        audienceSummary: this.audienceSummary,
        audienceText: this.summaryOriginText,
        // Same reasoning as the audience figures: the modal used to price the
        // dispatch from its own hardcoded table, so the confirmation screen and
        // the rail behind it quoted different money.
        unitPrice: this.summaryPricePerMessage,
        estimatedCost: this.summaryEstimatedCost,
        priceCurrency: this.summaryPriceCurrency,
        unpriced: this.summaryUnpriced,
        // What the modal says in place of a figure. A wallet-billed dispatch
        // repeats the rail's reason, so both screens call the send unpriced
        // (RF-33). An ordinary Cloud API one keeps the single text the modal has
        // always shown there, whichever of its three reasons the rail gives.
        unpricedLabel: this.cloudApiBilledByWallet
          ? this.summaryUnpricedLabel
          : 'campaign.campaignForm.priceNoHistory',
      },
      width: '600px',
    });

    dialogRef.afterClosed().subscribe((confirmedData: CampaingSaveConfirmData) => {
      if (!confirmedData) return;

      campaign.leadCount = confirmedData.leadCount;
      // The figure the operator was actually looking at when they confirmed.
      // Kept so "the number on screen is the number that goes out" can be
      // checked afterwards — the server's own count cannot verify itself.
      campaign.totalToSendShown = this.audienceSummary?.totalToSend;
      this.saveCampaign(campaign);
    });
  }

  prepareSaveCampaign(): Campaign {
    const form = { ...this.formGroup.value };
    const company = { _id: this.getIDCurrentCompany() } as Company;

    const campaign = {
      name: form.name,
      channel: form.channel,
      company: company,
      scheduledDate: new Date(),
      executionType: form.executionType || CampaignExecutionTypeEnum.IMEDIATE,
      audienceType: form.audienceType || CampaignAudienceTypeEnum.ALL_LEADS,
      audienceTags: form.tags || null,
      ignoredAudienceTags: form.ignoredTags || null,
      ignoredAudienceContactGroup: form.ignoredContactGroups || null,
      status: CampaignStatusEnum.WAITING,
      delayInSeconds: form.delayInSeconds || 0,
      operator: this.getCurrentUserUser(),
      enabled: form.enabled || false,
    } as Campaign;

    if (this.selectedChannel.type === ChannelTypeEnum.CLOUD_API) {
      campaign.metadata = {
        cloudAPIData: {
          template: this.selectedTemplate,
          headerParams: this.headerParams.controls.map((control: FormGroup) => control.get('value').value),
          bodyParams: this.bodyParams.controls.map((control: FormGroup) => control.get('value').value),
          buttonParams: this.buttonParams.controls.map((control: FormGroup) => control.get('value').value),
        },
      };
    } else if (this.selectedChannel.type === ChannelTypeEnum.Z_API) {
      const zapiGroup = this.getZAPIFormGroup;
      const messageType = zapiGroup.get('messageType')?.value;

      if (messageType === 'buttons') {
        campaign.metadata = {
          zapiData: {
            messageType: zapiGroup.get('messageType')?.value,
            buttonsTitle: zapiGroup.get('buttonsTitle')?.value || '',
            buttonsMessage: zapiGroup.get('buttonsMessage')?.value || '',
            buttonsFooter: zapiGroup.get('buttonsFooter')?.value || '',
            buttonActions: (zapiGroup.get('buttonActions') as FormArray)?.value || [],
          } as ZAPIData,
        };
      } else {
        campaign.metadata = {
          zapiData: {
            midia: zapiGroup.get('midia')?.value || '',
            type: zapiGroup.get('type')?.value || '',
            contentType: zapiGroup.get('contentType')?.value || '',
            message: zapiGroup.get('message')?.value || ''
          }
        };
      }

    } else if (this.selectedChannel.type === ChannelTypeEnum.RCS_API) {
      campaign.metadata = {
        rcsData: form.rcsDataGroup,
      };
    }
    this.prepareScheduledCampaign(form, campaign);
    this.prepareAudienceTagsCampaign(form, campaign);
    this.prepareAudienceContactGroupsCampaign(form, campaign);
    this.prepareAudienceFileCampaign(form, campaign);

    return campaign;
  }

  /**
   * The list restores its cached rows on re-entry and does not re-fetch, so a
   * campaign saved here would be missing (or stale) on the screen behind us.
   * Dropping the cached state makes the list load itself again.
   */
  private invalidateCampaignList(): void {
    this.listCache.clear(CacheKeyEnum.CAMPAIGN_LIST);
  }

  private async saveCampaign(campaign: Campaign) {
    if (this.isNew) {
      this.campaignService.create(campaign).subscribe({
        next: (value) => {
          this.alertService.success(this.t('campaign.campaignForm.campaignCreatedSuccess'));
          this.invalidateCampaignList();
          this.goBack();
        },
      });
    } else {
      this.campaignService.update(this.campaign._id, campaign).subscribe({
        next: (value) => {
          this.alertService.success(this.t('campaign.campaignForm.campaignUpdatedSuccess'));
          this.invalidateCampaignList();
          this.goBack();
        },
      });
    }
  }

  private prepareAudienceTagsCampaign(form: any, campaign: Campaign) {
    if (form.audienceType === CampaignAudienceTypeEnum.FILE) return;
    if (form.audienceType === CampaignAudienceTypeEnum.TAGS) {
      campaign.audienceType = CampaignAudienceTypeEnum.TAGS;
      campaign.audienceTags = form.tags;
      campaign.ignoredAudienceContactGroup = [];
      campaign.ignoredAudienceTags = [];
    } else if (form.audienceType !== CampaignAudienceTypeEnum.CONTACT_GROUP) {
      campaign.audienceTags = null;
      campaign.ignoredAudienceTags = form.ignoredTags ?? [];
      campaign.ignoredAudienceContactGroup = form.ignoredContactGroups ?? [];
    }
  }

  /**
   * A file audience carries the list and nothing else. Clearing the tag and
   * group fields here — as well as on the server — keeps "who receives this"
   * answerable from a single field.
   */
  private prepareAudienceFileCampaign(form: any, campaign: Campaign) {
    if (form.audienceType !== CampaignAudienceTypeEnum.FILE) {
      campaign.audienceFile = null;
      return;
    }
    campaign.audienceType = CampaignAudienceTypeEnum.FILE;
    campaign.audienceFile = form.audienceFile;
    campaign.audienceTags = [];
    campaign.audienceContactGroup = [];
    campaign.ignoredAudienceTags = [];
    campaign.ignoredAudienceContactGroup = [];
  }

  private prepareAudienceContactGroupsCampaign(form: any, campaign: Campaign) {
    if (form.audienceType === CampaignAudienceTypeEnum.FILE) return;
    if (form.audienceType === CampaignAudienceTypeEnum.CONTACT_GROUP) {
      campaign.audienceType = CampaignAudienceTypeEnum.CONTACT_GROUP;
      campaign.audienceContactGroup = form.contactGroups;
      campaign.ignoredAudienceTags = [];
      campaign.ignoredAudienceContactGroup = [];
    } else if (form.audienceType !== CampaignAudienceTypeEnum.TAGS) {
      campaign.audienceContactGroup = null;
      campaign.ignoredAudienceContactGroup = form.ignoredContactGroups ?? [];
      campaign.ignoredAudienceTags = form.ignoredTags ?? [];
    }
  }

  private prepareScheduledCampaign(form: any, campaign: Campaign) {
    if (form.executionType === CampaignExecutionTypeEnum.SCHEDULED) {
      const scheduledDateHr = form.scheduledHour.split(':');
      campaign.executionType = CampaignExecutionTypeEnum.SCHEDULED;
      campaign.scheduledDate = new Date(form.scheduledDay);
      campaign.scheduledDate.setHours(scheduledDateHr[0]);
      campaign.scheduledDate.setMinutes(scheduledDateHr[1]);
    } else {
      campaign.scheduledDate = new Date();
    }
  }

  public get headerComponent(): HeaderComponentObject {
    return <HeaderComponentObject>this.selectedTemplate?.components.find(c => c.type === 'HEADER');
  }

  public get bodyComponent(): BodyComponentObject {
    return <BodyComponentObject>this.selectedTemplate?.components.find(c => c.type === 'BODY');
  }

  public get footerComponent(): FooterComponentObject {
    return <FooterComponentObject>this.selectedTemplate?.components.find(c => c.type === 'FOOTER');
  }

  public get buttonsComponent(): ButtonsComponentObject {
    return <ButtonsComponentObject>this.selectedTemplate?.components.find(c => c.type === 'BUTTONS');
  }


  get headerParams() {
    return this.formGroup.get('cloudAPIDataGroup.headerParams') as FormArray;
  }

  get bodyParams() {
    return this.formGroup.get('cloudAPIDataGroup.bodyParams') as FormArray;
  }

  get buttonParams() {
    return this.formGroup.get('cloudAPIDataGroup.buttonParams') as FormArray;
  }

  get headerValue() {
    return this.headerParams.controls[0].get('value').value;
  }

  get templateTotalCharacters(): number {
    if (!this.selectedTemplate || !this.bodyComponent) return 0;

    let bodyText = this.bodyComponent.text || '';

    this.bodyParams.controls.forEach((control, index) => {
      const value = control.get('value')?.value || '';
      const placeholder = `{{${index + 1}}}`;
      bodyText = bodyText.replace(placeholder, value);
    });

    return bodyText.length;
  }

  get isTemplateOverLimit(): boolean {
    return this.templateTotalCharacters > this.META_BODY_CHARACTER_LIMIT;
  }

  onSelectTemplate(template: TemplateMessageObject): void {
    this.selectedTemplate = template;
    this.loadCloudApiTemplatePrice();

    (this.formGroup.get('cloudAPIDataGroup.headerParams') as FormArray).clear();
    (this.formGroup.get('cloudAPIDataGroup.bodyParams') as FormArray).clear();
    (this.formGroup.get('cloudAPIDataGroup.buttonParams') as FormArray).clear();
    this.formGroup.get('cloudAPIDataGroup.template').setValue(this.selectedTemplate);

    this.buttonWithExampleIndices = [];

    if (this.headerComponent) {
      if (this.isMedia) {
        (this.formGroup.get('cloudAPIDataGroup.headerParams') as FormArray).push(
          new FormGroup({
            value: new FormControl(null, [Validators.required]),
          })
        );
      } else {
        if (this.headerComponent.example?.header_text?.length > 0 || this.headerComponent.example?.header_handle?.length > 0) {
          (this.formGroup.get('cloudAPIDataGroup.headerParams') as FormArray).push(
            new FormGroup({
              value: new FormControl(null, [Validators.required]),
            })
          );
        }
      }
    }

    if (this.bodyComponent) {
      const numberVariables = this.bodyComponent.example?.body_text[0]?.length;
      for (let i = 1; i <= numberVariables; i++) {
        (this.formGroup.get('cloudAPIDataGroup.bodyParams') as FormArray).push(
          new FormGroup({
            value: new FormControl(null, [Validators.required]),
          })
        );
      }
    }

    if (this.buttonsComponent) {
      if (this.buttonsComponent?.buttons?.length > 0) {
        this.buttonsComponent.buttons.forEach((button, index) => {
          this.buttonWithExampleIndices.push(index);
          const disabled = !button?.['example'];
          const value = !disabled ? '' : button.text || '';
          (this.formGroup.get('cloudAPIDataGroup.buttonParams') as FormArray).push(
            new FormGroup({
              value: new FormControl({ value, disabled }, [Validators.required]),
            })
          );
        });
      }
    }
  }

  onOptionSelected(section: 'headers' | 'bodys', optionText: string, index: number): void {
    const idx = index - 1;
    let arr: FormArray;
    if (section === 'headers') {
      arr = this.headerParams;
    } else {
      arr = this.bodyParams;
    }
    const grp = arr?.at(idx) as FormGroup;
    if (grp && grp.get('value')) {
      grp.get('value')?.setValue(optionText);
    }

    const selectedOption = this.variableOptions.find(opt => opt.text === optionText);
    if (selectedOption && selectedOption.action) {
      selectedOption.action(section, index);
    }
  }

  onInputChange(section: 'headers' | 'bodys', index: number): void {
    const idx = index - 1;
    let arr: FormArray;
    if (section === 'headers') {
      arr = this.headerParams;
    } else {
      arr = this.bodyParams;
    }
    const value = arr?.at(idx)?.get('value')?.value || '';
    if (!value) {
      this.filteredOptions = this.variableOptions.slice();
    } else {
      this.filteredOptions = this.filterOptions(value);
    }
  }

  filterOptions(value: string): Array<{ icon: string, text: string, action: Function }> {
    const filterValue = value.toLowerCase();
    return this.variableOptions.filter(option =>
      option.text.toLowerCase().includes(filterValue)
    );
  }

  selectVarNameContactLead(section: 'headers' | 'bodys', varNumber: number): void {
    const idx = varNumber - 1;
    let arr: FormArray;
    if (section === 'headers') {
      arr = this.headerParams;
    } else {
      arr = this.bodyParams;
    }
    const grp = arr?.at(idx) as FormGroup;
    if (grp && grp.get('value')) grp.get('value')?.setValue('{{NOME}}');
  }

  selectVarPhoneContactLead(section: 'headers' | 'bodys', varNumber: number): void {
    const idx = varNumber - 1;
    let arr: FormArray;
    if (section === 'headers') {
      arr = this.headerParams;
    } else {
      arr = this.bodyParams;
    }
    const grp = arr?.at(idx) as FormGroup;
    if (grp && grp.get('value')) grp.get('value')?.setValue('{{TELEFONE}}');
  }

  selectVarEmailContactLead(section: 'headers' | 'bodys', varNumber: number): void {
    const idx = varNumber - 1;
    let arr: FormArray;
    if (section === 'headers') {
      arr = this.headerParams;
    } else {
      arr = this.bodyParams;
    }
    const grp = arr?.at(idx) as FormGroup;
    if (grp && grp.get('value')) grp.get('value')?.setValue('{{EMAIL}}');
  }

  get isMedia() {
    return (!!this.headerComponent && this.headerComponent!.format !== 'TEXT');
  }

  get getCloudAPIFormGroup(): FormGroup {
    return this.formGroup.get('cloudAPIDataGroup') as FormGroup;
  }

  get getZAPIFormGroup(): FormGroup {
    return this.formGroup.get('zapiDataGroup') as FormGroup;
  }

  get getRCSFormGroup(): FormGroup {
    return this.formGroup.get('rcsDataGroup') as FormGroup;
  }

  /**
   * O comportamento do agente RCS é do CANAL, não uma escolha do disparo.
   *
   * O tipo é homologado junto ao Google e às operadoras e não muda depois, e
   * cada agente homologado aparece aqui como um canal próprio — então trocar de
   * comportamento é trocar de canal, não marcar outra opção. Antes isto lia um
   * radio que o operador preenchia, com a segunda opção fixa em "indisponível".
   */
  isRcsAgentConversational(): boolean {
    return this.isRcsChannelConversational(this.selectedChannel);
  }

  /**
   * POR ENQUANTO todo canal RCS é conversacional.
   *
   * O #1367 encerrou a distinção no produto: `validateChannelRCSAPI` carimba
   * `metadata.conversational = true` em toda escrita, e a chave só continua na
   * entidade para o caso de a separação voltar sem migração de dados. Aquele
   * commit não migrou o histórico, então canal RCS salvo antes dele ainda
   * guarda `false` — ler o flag aqui descreveria como "não conversacional" um
   * canal que o produto já trata como conversacional.
   *
   * A regra fica NESTA tela e não em `isRCSConversational` do lib-trend-core
   * de propósito: aquele helper é o espelho do backend, que continua lendo o
   * flag para escolher a rota de envio. Mudá-lo aqui dessincronizaria os dois.
   * Quando os canais legados forem carimbados, este método volta a delegar
   * para o helper.
   */
  isRcsChannelConversational(channel: Channel): boolean {
    return channel?.type === ChannelTypeEnum.RCS_API;
  }

  isChannelRcs(channel: Channel): boolean {
    return channel?.type === ChannelTypeEnum.RCS_API;
  }

  get cloudAPIData(): CloudAPIData {
    return <CloudAPIData>{
      template: this.selectedTemplate,
      headerParams: this.headerParams.controls.map((control: FormGroup) => control.get('value').value),
      bodyParams: this.bodyParams.controls.map((control: FormGroup) => control.get('value').value),
      buttonParams: this.buttonParams.controls.map((control: FormGroup) => control.get('value').value),
    };
  }

  private openModalAlert(title: string, message: string): void {
    this.alertService.warning(message, title);
  }

}
